import { z } from 'zod';
import { commandKeyOf } from '../utils/commandAssignment.util';
import { allocKeyOf, lineKeyOf } from '../utils/fillRef.util';
import { MAX_DUTY_DAYS, WEEKDAYS, lastDutyDate } from '../utils/slot.util';
import { LINE_KEY_REGEX } from '../utils/sharedColumn.util';
import { awaitedCommandSchema, commandAskInputSchema, commandAskSchema } from './commandSeat.schema';
import { createPaginationSchema, objectIdSchema } from './common.schema';
import { presenceTallySchema } from './dutyEvent.schema';
import { ARRANGEMENT_GROUP_KINDS, ARRANGEMENT_GROUP_ROLES } from '../constants/arrangementGroup.constant';
import { arrangementStatusWordOf, type ArrangementStatusWord } from '../constants/arrangementStatusWord.constant';
import { FILL_STATES } from '../constants/fillState.constant';
import { RAISER_LEVELS } from '../constants/raiserLevel.constant';
import {
  ARRANGEMENT_BOOKLETS,
  ARRANGEMENT_TYPE_ANY,
  ARRANGEMENT_TYPE_KEYS,
  ARRANGEMENT_TYPE_NONE,
} from '../constants/arrangementType.constant';
import { showLifecycleSchema } from './lifecycle.schema';
import { officerSummarySchema } from './officer.schema';
import { orgUnitSummarySchema } from './orgUnit.schema';
import { placeSummarySchema } from './place.schema';
import { policySummarySchema } from './policy.schema';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE POLICE ARRANGEMENT — the order (PA_REQUIREMENTS §4.1, FR-5.2.*).
 *
 *  ⚠️ [amended D4] A PA CARRIES ONLY HEADER FIELDS AND A LIST OF DUTY POINTS.
 *
 *  Until D4 it carried one place list, one schedule, one policy version, one requirement
 *  grid and one command block, and FR-5.2.3a said so outright: "one PA carries ONE
 *  schedule, applying to all its places… when places genuinely need different timings,
 *  that is two PAs". The client has said no. A DUTY POINT now owns each of those, and the
 *  arrangement keeps only what is genuinely shared across all of them — its number, its
 *  order date, its title, its instructions and its supporting arrangements.
 *
 *  ⚠️ CONCURRENCY IS STRUCTURAL NOW, NOT A FLAG. Two places at once is two duty points;
 *  one team walking three places in order is ONE duty point holding an ordered route. So
 *  `LOCATION_MODES`, `deriveLocationMode` and `oneAfterAnother` have LEFT THIS FILE — they
 *  moved to `standingOrder.schema.ts`, their only remaining consumer. A mode that selects
 *  between one branch is not a parameter, and the control that set it is unrepresentable
 *  rather than merely hidden.
 *
 *  ⚠️ A DUTY POINT SNAPSHOTS A `policyVersionId`, NEVER A `policyId` ALONE.
 *  Publishing a policy freezes its version for ever precisely so that editing a policy
 *  next month cannot rewrite last month's order. `policyId` travels alongside for the
 *  list's filter and for a human-readable name, and is never what the columns come from.
 *
 *  ⚠️ THE ORDER DATE IS ADMINISTRATIVE. `paDate` is what the sheet is headed with and what
 *  an arrangement GROUP keys on. It answers nothing about when duty happens — that is
 *  `firstDutyDate`/`lastDutyDate`, DERIVED from the duty points and never typed.
 *
 *  ⚠️ COMMAND IS NOT PART OF THE REQUIREMENT, ANYWHERE IN THIS FILE.
 *  `requirementLines` and `commandEntries` are siblings, not parent and child, and no
 *  count in this file can reach a command entry. FR-5.2.6b closes OD-9 because everybody
 *  assumes command counts toward force; `api/tests/integration/commandRoleIsolation.test.ts`
 *  holds the line mechanically, because a comment cannot.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

// ── Vocabulary ───────────────────────────────────────────────────────────────────

/**
 * PA_REQUIREMENTS Appendix B. P3a implements `DRAFT → PUBLISHED` and `→ CANCELLED`;
 * the rest are reachable and are written by P4 and P5.
 *
 * ⚠️ `FULLY_MAPPED` IS NOT HERE, AND MUST NOT BE ADDED. FR-5.3.6a makes it DERIVED from
 * the assignments beneath a deployment, and a stored copy of a rollup is a copy that goes
 * wrong the first time a division is added to a live PA.
 */
export const ARRANGEMENT_STATUSES = [
  'DRAFT',
  'PUBLISHED',
  'ALLOCATION_IN_PROGRESS',
  'ACTIVE',
  'CLOSED',
  'CANCELLED',
] as const;
export type ArrangementStatus = (typeof ARRANGEMENT_STATUSES)[number];

/**
 * ⚠️ [C5b §3] THE INVERSE OF `arrangementStatusWordOf`, DERIVED FROM IT — NEVER A SECOND TABLE.
 *
 * The status filter shows WORDS and sends statuses, so `Published` must find `PUBLISHED` AND
 * `ALLOCATION_IN_PROGRESS`. A hand-written `Record<ArrangementStatusWord, ArrangementStatus[]>`
 * would be a second expression of one reduction and would silently miss the next status added —
 * `statusesOfOfficerDutyState`'s argument, one surface over. `arrangement.schema.test.ts` holds the
 * round trip over every status.
 *
 * ⚠️ HERE RATHER THAN BESIDE THE FORWARD MAP, for that function's reason too: the forward map is
 * in `constants/` with a type-only import, and deriving the inverse needs the runtime tuple.
 */
export const statusesOfArrangementStatusWord = (word: ArrangementStatusWord): ArrangementStatus[] =>
  ARRANGEMENT_STATUSES.filter((status) => arrangementStatusWordOf(status) === word);

/**
 * ⚠️ THE P3b SEAM, AND IT HAS TO SHIP NOW TO BE ADDITIVE LATER.
 *
 * Round-the-clock arrangements are P3b. What "the list is prepared for a second tab"
 * actually means in code is this: the discriminator exists, every arrangement carries
 * `STANDARD`, the list filters on it, and the index leads with the scope so a second
 * value costs no migration. Adding an enum member to a populated collection later means
 * backfilling every row; adding one to a collection where every row already declares its
 * kind means adding a member.
 *
 * P3a writes `STANDARD` and nothing else, and there is deliberately no UI to choose.
 */
export const ARRANGEMENT_KINDS = ['STANDARD', 'ROUND_THE_CLOCK'] as const;
export type ArrangementKind = (typeof ARRANGEMENT_KINDS)[number];

/**
 * ⚠️ [P3b.1] "SOMEBODY HAS STARTED WORK UNDER THIS ORDER."
 *
 * The standing-order generator may replace a future arrangement it made, and may NEVER
 * touch one that is already being filled. This is the status half of that question — the
 * other half is any deployment whose status has left `PENDING`.
 *
 * ⚠️ WRITTEN OUT, NOT SLICED OFF `ARRANGEMENT_STATUSES` BY POSITION. A new status member
 * must be a DECISION taken here, not a silent reclassification that reaches into other
 * people's rosters. `arrangement.schema.test.ts` pins the complement.
 */
export const ARRANGEMENT_STATUSES_CARRYING_WORK = [
  'ALLOCATION_IN_PROGRESS',
  'ACTIVE',
  'CLOSED',
  'CANCELLED',
] as const satisfies readonly ArrangementStatus[];

/**
 * ⚠️ [LIFECYCLE] "SOMEBODY HAS BEEN TOLD ABOUT THIS ORDER."
 *
 * §3a: archiving a PUBLISHED arrangement is a CANCELLATION, not filing — it must state how
 * many officers are affected and emit cancellation events. Archiving a DRAFT is silent,
 * because nobody has been told anything about a draft.
 *
 * ⚠️ THIS IS A DECISION, NOT `status !== 'DRAFT'`. The two are the same set today and the
 * inequality would be free — but "a draft emits nothing" would then be true by ARITHMETIC
 * rather than by intent, and it would break silently the day a status is added that also
 * predates anyone being told. Written out for the reason the tuple above is written out:
 * a new status member must be a decision about whether archiving it texts three hundred
 * officers. `arrangement.schema.test.ts` pins the complement.
 */
export const ARRANGEMENT_STATUSES_ISSUED = [
  'PUBLISHED',
  'ALLOCATION_IN_PROGRESS',
  'ACTIVE',
  'CLOSED',
  'CANCELLED',
] as const satisfies readonly ArrangementStatus[];

export const RECURRENCE_KINDS = ['ONE_DAY', 'DATE_RANGE', 'WEEKDAYS'] as const;
export const TIMING_KINDS = ['NO_FIXED_TIME', 'SHIFTS'] as const;

/**
 * How a command role's named officers map onto one shift (FR-5.2.5, the P3a brief §3.4).
 *
 * ⚠️ THE SEMANTICS ARE THE CLIENT'S STATED INTENT AND ARE NOT YET CONFIRMED FOR
 * EXECUTION. They are interpreted in exactly one place —
 * `resolveCommandForSlot` in `commandAssignment.util.ts` — so P5 reads one function
 * rather than a rule scattered across a screen and a service.
 */
export const COMMAND_SLOT_MODES = ['ALL', 'ANY', 'SPECIFIC'] as const;
export type CommandSlotMode = (typeof COMMAND_SLOT_MODES)[number];

// ── Shape bounds ─────────────────────────────────────────────────────────────────

/**
 * ⚠️ THESE BOUND THE INPUT'S SHAPE. THEY DO NOT BOUND THE PRODUCT.
 *
 * 12 places × 6 shifts × 366 days is an absurd payload this schema would happily accept,
 * and the deployment count it implies is the thing that actually has to be refused. Only
 * the service knows the product, so `MAX_DEPLOYMENTS_PER_ARRANGEMENT` lives there and
 * throws. These stop a hostile or fat-fingered body before it reaches one.
 *
 * ⚠️ [P3b.1] `SCHEDULE_DAYS: 92` WAS DELETED FROM THIS OBJECT RATHER THAN ENFORCED, and
 * the choice was forced rather than tidy. It had exactly one occurrence in the repository —
 * its own declaration — and the CHANGELOG's standing instruction was "enforce it everywhere
 * or delete it". Enforcing it would have RETROACTIVELY REFUSED STORED, LEGAL SCHEDULES:
 * `MAX_RECURRENCE_SPAN_DAYS`'s own header describes a Mondays-only order running from
 * 2026-01-01 to 2027-06-30 — 546 calendar days, 78 duty days — which is legal today and is
 * pinned as a regression test. How long a schedule may RUN is bounded by `MAX_DUTY_DAYS`
 * (366) at one end and `MAX_DEPLOYMENTS_PER_ARRANGEMENT` (500) at the other, and both of
 * those have real consumers that really throw.
 */
export const ARRANGEMENT_LIMITS = {
  /**
   * ⚠️ [amended D4] The bound that replaced `LOCATIONS` as the arrangement-level one.
   *
   * The document has to stay inside 16 MB, and a duty point is the expensive thing on it:
   * worst case one point is ~230 KB (40 requirement lines × 30 allocations, 20 command
   * entries, 40 slot overrides), so 24 of them is ~5.5 MB. Realistic is ~3 KB each. The
   * bound that actually bites first is `MAX_DEPLOYMENTS_PER_ARRANGEMENT` — 24 duty points
   * each producing one duty is refused there long before bytes matter.
   */
  DUTY_POINTS: 200,
  /**
   * ⚠️ [amended D4] WAS `LOCATIONS: 12`, AND IT NOW BOUNDS A ROUTE. One duty point's
   * ordered list of stops, not an arrangement's whole place list — which is now the sum
   * across duty points and is bounded by `DUTY_POINTS` above.
   */
  PLACES_PER_DUTY_POINT: 48,
  SHIFTS: 24,
  REQUIREMENT_LINES: 120,
  ALLOCATIONS_PER_LINE: 60,
  /** Was an inline `.max(20)` on the base schema; named here now that it is per duty point. */
  COMMAND_ENTRIES: 40,
  COMMAND_OFFICERS_PER_ROLE: 40,
  INSTRUCTIONS: 600,
  SUPPORTING: 600,
  /**
   * ⚠️ [C1b §4] ASSOCIATED UNITS — a creator-chosen, cross-division grant of SIGHT.
   *
   * Fifty ObjectIds is ~1.2 KB stored, and each one is closed into `visible_org_unit_ids`
   * with its ancestors and descendants — so the array that actually grows is that one, by
   * roughly four entries per association against a real establishment of ~170 nodes. The
   * bound exists because there is no operational reading of "this order is also the business
   * of more than fifty other units": past that, the answer is a wider allocation.
   */
  ASSOCIATED_UNITS: 50,
  RESPONSIBILITIES: 200,
  /**
   * ⚠️ [amended D4.3] PER DUTY POINT, AND DELIBERATELY MUCH SMALLER THAN THE PA-LEVEL PAIR.
   *
   * The bound above is multiplied by `DUTY_POINTS` here, and the arithmetic is the whole
   * argument. One supporting arrangement is up to 120 + 20 x 300 = about 6.1 KB, so reusing
   * `SUPPORTING: 20` per point would add roughly 122 KB x 24 = 2.9 MB to a document whose
   * worst case is already about 5.5 MB; `INSTRUCTIONS: 100` would add another 50 KB x 24 =
   * 1.2 MB. Together that is about 9.6 MB against a 16 MB hard limit, for a field almost
   * every order leaves empty.
   *
   * At 20 and 5 the same worst case is 10 KB + 31 KB = about 41 KB per point, roughly 1 MB
   * across 24 - and the operational claim is unchanged, because point-specific instructions
   * are the standing orders for ONE place. A hundred of them is a document, not a duty point.
   *
   * Same move, and the same reason, as D4 dropping `SLOT_OVERRIDES` from 100 to 40 when it
   * became per-point.
   */
  INSTRUCTIONS_PER_DUTY_POINT: 200,
  SUPPORTING_PER_DUTY_POINT: 200,
  /**
   * ⚠️ [amended D4] LOWERED FROM 100, AND NOW PER DUTY POINT. It bounds the overrides on
   * ONE point's ONE schedule, where 100 was always slack — and it is the term that grows
   * the document fastest, since each override carries a whole requirement grid.
   */
  SLOT_OVERRIDES: 40,
  /**
   * [C4a §4] A duty point's ZONE — free text an operator types and the next card autocompletes from.
   * A label, not a document: sixty characters holds "North Zone — Sector 3 (Esplanade side)".
   */
  ZONE: 60,
  /**
   * ⚠️ [PLACE-500] ONE PLACE'S NAME — a duty place, a reporting place, a standing order's location, and a
   * template decision's typed place. It was a bare `200` in seven files; the 2026 booklet prints duty places
   * of 207 and 269 characters ("Along Rashbehari Avenue from …"), and shortening a printed place to fit is a
   * rewrite of the order. Every model's `maxlength` and the decision form read THIS number, so they cannot
   * drift. A one-place route keys at most `t:` + 500 characters, inside `deployments.location_key`'s 2,000.
   */
  PLACE_NAME: 500,
  /*
   * ⚠️ [C5c §4] AND NO `SECTOR` KEY, WHICH IS THE DECISION RATHER THAN AN OVERSIGHT. The client asked
   * for a sector with no length limit, so `dutyPointInputSchema.sector` carries no `.max` and the
   * model no `maxlength`. What still bounds it is what bounds every byte of a request: `BODY_LIMIT`
   * refuses the body before zod sees it (a 413), and the 16 MB BSON document limit sits behind that.
   * A key here with no consumer would read as a bound nobody enforces, which is worse than none.
   */
  /**
   * ⚠️ [C5c §2] HOW MANY COLUMNS ONE ORDER MAY SHARE ACROSS ITS DUTY POINTS.
   *
   * One entry per shared column, and a column is one requirement line on its anchor card — so the
   * natural bound is `REQUIREMENT_LINES`. A stash row REPLACES the card line it came from, so the
   * document does not grow: the worst case (40 entries × 23 stash rows × 30 allocations) is data
   * the cards already held before sharing moved it.
   */
  SHARED_COLUMNS: 40,
} as const;

// ── Primitives ───────────────────────────────────────────────────────────────────

/** `YYYY-MM-DD`, an IST calendar date. Never an instant: a duty date is a business day. */
export const calendarDateSchema = z.iso.date();

/**
 * `HH:mm`, zero-padded, no seconds.
 *
 * ⚠️ THE PADDING IS LOAD-BEARING. `slot.util.ts` decides whether a shift crosses midnight
 * with a STRING comparison, which is chronological only while every time is `HH:mm`.
 * `"7:00"` is refused here so that comparison can never be wrong.
 */
export const timeOfDaySchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use a 24-hour time like 20:00');

const trimmedText = (max: number) => z.string().trim().min(1).max(max);

// ── Locations ────────────────────────────────────────────────────────────────────

/**
 * ⚠️ FREE TEXT IS ALWAYS PERMITTED, AND THAT IS THE POINT.
 *
 * "Naka point, Gate 4" is a real duty location no gazetteer knows, and a form that
 * refuses it is a form the operator abandons. So a row carries a `name` always and a
 * `placeId` only when one was picked from the master. Nothing here writes to the place
 * master — a NODAL_READ user may raise an arrangement and holds no `place.write`.
 */
export const dutyPointPlaceInputSchema = z.object({
  placeId: objectIdSchema.nullish(),
  name: trimmedText(ARRANGEMENT_LIMITS.PLACE_NAME),
  note: z.string().trim().max(300).nullish(),
});

export const dutyPointPlaceSchema = z.object({
  placeId: objectIdSchema.nullable(),
  name: z.string(),
  note: z.string().nullable(),
  displayOrder: z.number().int().min(0),
  /** Resolved for display. `null` when the row is free text, or the place was removed. */
  place: placeSummarySchema.nullable(),
});

// ── Schedule ─────────────────────────────────────────────────────────────────────

/**
 * ⚠️ TIED TO `recurrenceEndSchema` BY A TEST, NOT BY HOPE. `RECURRENCE_KINDS` is the
 * cautionary tale: it has exactly two references in the repository — its own declaration
 * and the Mongoose `enum:` — so adding a member widens what the database accepts and
 * changes nothing else. `arrangement.schema.test.ts` asserts this tuple equals
 * `recurrenceEndSchema.options`'s discriminators, and does the same for `RECURRENCE_KINDS`.
 */
export const RECURRENCE_END_KINDS = ['ON_DATE', 'AFTER_N'] as const;
export type RecurrenceEndKind = (typeof RECURRENCE_END_KINDS)[number];

export const recurrenceEndSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('ON_DATE'), endDate: calendarDateSchema }),
  z.object({
    kind: z.literal('AFTER_N'),
    /*
     * ⚠️ NO `.default()`, HERE OR ANYWHERE INSIDE A RECURRENCE ARM. MEASURED: `.partial()`
     * operates on TOP-LEVEL keys only and does not recurse, so parsing `{}` never enters an
     * arm — `updateSchema.test.ts` is structurally BLIND to a default in here, and it would
     * still fire on every PATCH carrying a schedule. The initial value is the form's job.
     */
    occurrences: z.number().int().min(1, 'Repeat at least once').max(MAX_DUTY_DAYS),
  }),
]);

/*
 * ⚠️ FACTORED, SO THE RESTATEMENT COUNT GOES DOWN RATHER THAN UP. Both ranged arms carry
 * exactly `startDate` + `ends`; only the weekday filter distinguishes them.
 */
const rangedRecurrence = { startDate: calendarDateSchema, ends: recurrenceEndSchema };

export const recurrenceInputSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('ONE_DAY'), date: calendarDateSchema }),
  z.object({ kind: z.literal('DATE_RANGE'), ...rangedRecurrence }),
  z.object({
    kind: z.literal('WEEKDAYS'),
    ...rangedRecurrence,
    /*
     * No RRULE anywhere near the operator: seven chips, and this is what they produce.
     *
     * ⚠️ `z.literal(TUPLE)`, NOT `z.enum(TUPLE)`. Measured: in zod 4 `z.enum` over a
     * tuple of NUMBERS accepts nothing at all — `z.enum([1,2,3]).safeParse(1)` fails —
     * so a weekday recurrence would have been impossible to submit, with no error at
     * authoring time and nothing in the type to say so. `z.literal` over the same tuple
     * is correct, and rejects `9`.
     *
     * ⚠️ AND THE `.min(1)` IS WHAT KEEPS THE ENGINE'S COUNT-TERMINATED WALK FINITE FROM
     * A VALID REQUEST. It is stated in exactly ONE arm; a second arm carrying weekdays
     * would have had to repeat it or relax it.
     */
    weekdays: z.array(z.literal(WEEKDAYS)).min(1, 'Choose at least one day of the week').max(7),
  }),
]);

export const shiftInputSchema = z
  .object({
    /**
     * ⚠️ THE SHIFT'S STABLE IDENTITY, AND IT IS WHAT `slotKey` IS BUILT FROM.
     *
     * Absent means "new, mint me one"; present means "this is the shift you already
     * know". Exactly the rule `rekeyCommandRoles` applies to a command role's key, and
     * for the same reason: a POSITIONAL key silently re-points every `slotOverride` and
     * every per-shift command row when a shift is inserted above them, so a detached
     * night duty quietly becomes a detached morning duty and nothing says so.
     */
    id: z.string().trim().min(1).max(24).optional(),
    name: z.string().trim().max(60).nullish(),
    startTime: timeOfDaySchema,
    endTime: timeOfDaySchema,
  })
  /*
   * Equal times are refused HERE, with a field-level message, rather than in the slot
   * engine — which treats them as spanning midnight so that it stays total and can never
   * emit a zero-length duty. Two guards, one user-facing.
   */
  .refine((shift) => shift.startTime !== shift.endTime, {
    path: ['endTime'],
    message: 'A shift cannot start and end at the same time',
  });

export const timingInputSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('NO_FIXED_TIME') }),
  z.object({
    kind: z.literal('SHIFTS'),
    shifts: z.array(shiftInputSchema).min(1, 'Add at least one shift').max(ARRANGEMENT_LIMITS.SHIFTS),
  }),
]);

export const scheduleInputSchema = z
  .object({ recurrence: recurrenceInputSchema, timing: timingInputSchema })
  /*
   * TWO recurrence cross-field rules, in the ONE place the first already lived.
   * `superRefine` rather than two `.refine`s so both carry their own field path, and so
   * `recurrenceEndSchema.options` stays reachable for the agreement test above.
   */
  .superRefine((schedule, ctx) => {
    const { recurrence } = schedule;
    if (recurrence.kind === 'ONE_DAY') return;

    if (recurrence.ends.kind === 'ON_DATE') {
      if (recurrence.startDate > recurrence.ends.endDate) {
        ctx.addIssue({
          code: 'custom',
          path: ['recurrence', 'ends', 'endDate'],
          message: 'The last day cannot come before the first',
        });
      }
      return;
    }

    /*
     * ⚠️ REFUSED HERE, WITH A FIELD PATH, RATHER THAN IN THE ENGINE.
     *
     * `slotsOf` and `arrangementShape.ts`'s `disclosure` both swallow every throw and read
     * it as "nothing yet", so an engine-side refusal renders as "0 duties" with no reason
     * given, and `previewPublish` then blocks with 'Set the dates and times.' — a message
     * that is a lie about what went wrong.
     *
     * ⚠️ AND IT IS THE ENGINE'S OWN ANSWER, NOT AN ESTIMATE. A closed form such as
     * `ceil(occurrences / perWeek) * 7` OVER-REFUSES: measured, 53 Sundays estimates 371
     * days and would be rejected, but actually lands inside 366. `lastDutyDate` is total,
     * so it cannot throw out of a refine — measured, a throw in a refine escapes
     * `safeParse` as the raw error and reaches the client as an opaque 500.
     */
    if (lastDutyDate(recurrence) === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['recurrence', 'ends', 'occurrences'],
        message:
          `That many repeats do not all fall within ${String(MAX_DUTY_DAYS)} days of the start date. ` +
          'Choose a smaller number, or add more days of the week.',
      });
    }
  });

// ── Requirement and allocation ───────────────────────────────────────────────────

/**
 * ⚠️ ROW-MAJOR HERE, COLUMN-MAJOR ON SCREEN.
 *
 * The grid a CP nodal sees has post types across the top and allocation sources down the
 * side; this stores one line per post type with its sources beneath it, exactly as
 * PA_REQUIREMENTS §4.1 draws it, because that is where P4 attaches an allocation status
 * and P5 attaches assignments. `web/src/lib/forceGrid.ts` transposes between the two, in
 * one function, so neither side has to think in the other's shape.
 */
export const allocationInputSchema = z.object({
  /** The DIVISION. Mandatory — FR-5.2.7. */
  divisionOrgUnitId: objectIdSchema,
  /** ⚠️ Blank means "any unit under this division", and that is the FAST PATH. */
  unitOrgUnitId: objectIdSchema.nullish(),
  count: z.number().int().min(0).max(9_999),
});

export const requirementLineInputSchema = z
  .object({
    /** Exactly one of these three identifies the column. Enforced by the refine below. */
    postTypeId: objectIdSchema.nullish(),
    vehicleTypeId: objectIdSchema.nullish(),
    /**
     * FR-5.2.6c — a one-off post row the policy does not carry. It applies to this
     * arrangement alone and NEVER writes back to the policy; creating a genuinely new
     * post type stays an admin action.
     */
    adHocLabel: z.string().trim().min(1).max(80).nullish(),
    /**
     * ⚠️ [amended C1c §4a, §4b] WHICH POSTS MAY FILL A ONE-OFF COLUMN. EMPTY MEANS ANY.
     *
     * A one-off column carries no post type, so `eligibleRankIdsFor` has nothing to read and
     * the eligibility search places NO rank predicate at all — which is right by default and
     * wrong for "Bomb squad". Naming post types here gives the column the union of their
     * eligible ranks, and that is the ONLY thing it does.
     *
     * ⚠️ THE DEFAULT IS ANY, AND THAT IS A REQUIREMENT RATHER THAN A CONVENIENCE (§4a): an
     * existing column's behaviour must not change under anyone, so an absent or empty array
     * means exactly what a pre-C1c line meant.
     *
     * ⚠️ IT NEVER WRITES BACK TO A POLICY. A one-off column has no policy identity — that is
     * the whole reason it is one-off — so this restricts the search for THIS column on THIS
     * arrangement and nothing else. It is not a route to a new post type.
     *
     * ⚠️ AND IT TAKES NO PART IN `lineKeyOf`. A slot's identity is `P:`/`V:`/`A:<slug>`, and
     * P4 hangs every assignment off it. Folding a restriction into that key would re-point
     * every named officer on the column the moment somebody edited it.
     */
    /*
     * ⚠️ `.optional()` AND NOT `.nullish()`, WHICH IS NOT A STYLE CHOICE. An empty array and an
     * absent one already both mean ANY, so `null` would be a THIRD spelling of one fact — and
     * §6's own test (does a caller who will never receive a value have to render something?)
     * answers no: there is nothing to put in a box.
     */
    restrictToPostTypeIds: z.array(objectIdSchema).max(ARRANGEMENT_LIMITS.ALLOCATIONS_PER_LINE).optional(),
    requiredCount: z.number().int().min(0).max(99_999),
    allocations: z.array(allocationInputSchema).max(ARRANGEMENT_LIMITS.ALLOCATIONS_PER_LINE),
  })
  .refine(
    (line) =>
      [line.postTypeId, line.vehicleTypeId, line.adHocLabel].filter((value) => value !== null && value !== undefined)
        .length === 1,
    { message: 'A requirement row names exactly one post type, vehicle type or one-off label' },
  )
  /*
   * ⚠️ [P4] A ONE-OFF LABEL MUST CONTAIN SOMETHING TO KEY ON.
   *
   * The same call `policy.schema.ts` makes about a command role whose slug would be empty:
   * a column that cannot be referred to is a column P4 cannot attach a name to. Without
   * this, `'!!!'` and `'???'` are two columns with one identity, and the duplicate check
   * below would report them as a duplicate — a true refusal with a confusing sentence.
   */
  .refine((line) => !line.adHocLabel || lineKeyOf(line) !== 'A:', {
    path: ['adHocLabel'],
    message: 'A one-off column name needs at least one letter or number',
  });

/**
 * ⚠️ [P4] TWO COLUMNS MAY NOT SHARE AN IDENTITY, AND NEITHER MAY TWO SOURCES WITHIN ONE.
 *
 * P4 attaches a named officer to `<lineKey>|<allocKey>#<ordinal>` (`fillRef.util.ts`),
 * which is what makes an assignment survive a column reorder. That only works if an
 * identity names one thing, so the uniqueness moves from an assumption to a rule here.
 *
 * ⚠️ THE SECOND HALF FIXES A BUG THAT PREDATES P4. `web/src/lib/forceGrid.ts`'s `toGrid`
 * already assumed allocation identities were unique within a line — it renders the FIRST
 * match for a source and ignores any other — while `allocatedCount` summed them all. Two
 * rows both saying "Central Division, any unit" therefore showed one number on screen and
 * counted a different one on the order. Refusing the shape is the fix; the grid never had
 * a way to render it.
 *
 * `superRefine` rather than two `.refine`s so each issue carries its own field path.
 */
export const requirementLinesInputSchema = z
  .array(requirementLineInputSchema)
  .max(ARRANGEMENT_LIMITS.REQUIREMENT_LINES)
  .superRefine((lines, ctx) => {
    const seenLines = new Map<string, number>();

    lines.forEach((line, index) => {
      const lineKey = lineKeyOf(line);
      const first = seenLines.get(lineKey);
      if (first === undefined) seenLines.set(lineKey, index);
      else {
        ctx.addIssue({
          code: 'custom',
          path: [index],
          message: `This column is already listed at position ${String(first + 1)}. Combine them into one row.`,
        });
      }

      const seenSources = new Map<string, number>();
      line.allocations.forEach((allocation, allocationIndex) => {
        const allocKey = allocKeyOf(allocation);
        const firstSource = seenSources.get(allocKey);
        if (firstSource === undefined) seenSources.set(allocKey, allocationIndex);
        else {
          ctx.addIssue({
            code: 'custom',
            path: [index, 'allocations', allocationIndex],
            message: 'This source is already listed on this column. Put the whole number on one row.',
          });
        }
      });
    });
  });

export const allocationSchema = z.object({
  /** ⚠️ STABLE. P4 writes a status against this id and P5 hangs assignments off it. */
  id: objectIdSchema,
  divisionOrgUnitId: objectIdSchema,
  unitOrgUnitId: objectIdSchema.nullable(),
  count: z.number().int().min(0),
  division: orgUnitSummarySchema.nullable(),
  unit: orgUnitSummarySchema.nullable(),
});

export const requirementLineSchema = z.object({
  postTypeId: objectIdSchema.nullable(),
  vehicleTypeId: objectIdSchema.nullable(),
  adHocLabel: z.string().nullable(),
  /** ⚠️ [amended C1c §4a] One-off columns only. Empty is ANY, which is every existing line. */
  restrictToPostTypeIds: z.array(objectIdSchema),
  requiredCount: z.number().int().min(0),
  displayOrder: z.number().int().min(0),
  allocations: z.array(allocationSchema),
});

// ── Command ──────────────────────────────────────────────────────────────────────

/**
 * ⚠️ THIS IS THE FILLED ROW, NOT THE DEFINITION.
 *
 * PA_REQUIREMENTS §4.1 names two different things: a `CommandRole` — the DEFINITION,
 * which lives on a policy version and nowhere else — and `Command[]`, the row an
 * arrangement fills in. This is the second. It references a definition by its stable
 * `roleKey` and carries no label, because the label lives on the frozen policy version
 * the arrangement already points at and therefore cannot drift.
 *
 * ⚠️ AND IT IS NEVER COUNTED. No total, no allocation, no fulfilment percentage, no
 * shortfall — not here, not in `requirementLineSchema`, not in the DTO below.
 * `commandRoleIsolation.test.ts` CLAIM 5 scans this vocabulary for arithmetic.
 */
export const commandSlotOverrideInputSchema = z.object({
  slotKey: z.string().trim().min(1).max(40),
  mode: z.enum(COMMAND_SLOT_MODES),
  /** Meaningful only when `mode` is `SPECIFIC`; ignored otherwise. */
  officerIds: z.array(objectIdSchema).max(ARRANGEMENT_LIMITS.COMMAND_OFFICERS_PER_ROLE),
});

/**
 * ⚠️ [amended D4.3] EXACTLY ONE OF TWO OPTIONAL STRINGS IS PRESENT.
 *
 * Lifted out of the refine below rather than inlined, and the reason is mechanical:
 * `commandRoleIsolation.test.ts` CLAIM 5 scans every line naming a command entry for
 * arithmetic and permits `.length` only as an emptiness test. `requirementLineInputSchema`
 * expresses the same rule as `[...].filter(...).length === 1`, which is legal there only
 * because that line names no command vocabulary. Here it would name `roleKey` and the guard
 * would refuse it — correctly, since the guard cannot tell an exclusivity check from a
 * headcount. Stating it as an exclusive-or on a line with no command vocabulary keeps the
 * ban absolute instead of forcing it to be weakened.
 */
const exactlyOneOf = (first: string | null | undefined, second: string | null | undefined): boolean =>
  (first === null || first === undefined) !== (second === null || second === undefined);

/**
 * ⚠️ [amended D4.3] A COMMAND ENTRY NAMES A POLICY ROLE **OR** CARRIES ITS OWN LABEL.
 *
 * FR-5.2.6c's one-off column, applied to command. An ad-hoc role belongs to THIS duty point
 * alone and is NEVER written back to the policy — the same contract, and for the same
 * reason, as `requirementLineInputSchema.adHocLabel` above.
 *
 * ⚠️ IT IS NOT A `CommandRole`, AND THAT DISTINCTION IS WHAT KEEPS THE GUARDS GREEN.
 * A role DEFINITION lives on a policy version and nowhere else (CLAIM 1 pins its vocabulary
 * to two files). This is the FILLED ROW, which CLAIM 5 governs, and it gains a label rather
 * than the policy gaining a role. `forkPolicyVersionForArrangement` deliberately has no
 * parameter through which a command role could be altered; nothing here changes that.
 *
 * ⚠️ AND IT THEREFORE HAS NO STABLE KEY ACROSS POLICIES. A label edited on one arrangement
 * is a different identity from the same words typed on another, so an ad-hoc role will
 * NEVER appear in a cross-policy "who commanded what" report. That is the accepted cost of
 * an ad-hoc, stated here rather than discovered later — a policy role is the answer for
 * anything that has to be comparable, and minting one is an admin action.
 *
 * ⚠️ STILL NEVER COUNTED. No total, no allocation, no fulfilment percentage, no shortfall.
 */
export const commandEntryInputSchema = z
  .object({
    /** Names a role on the frozen policy version. Absent when `adHocLabel` carries the name. */
    roleKey: z.string().trim().min(1).max(40).nullish(),
    /** This duty point only. `adHocLabel`'s twin on a requirement line, deliberately. */
    adHocLabel: z.string().trim().min(1).max(80).nullish(),
    officerIds: z.array(objectIdSchema).max(ARRANGEMENT_LIMITS.COMMAND_OFFICERS_PER_ROLE),
    /** Absent means `ALL` on every slot, which is the default and the common case. */
    perSlot: z.array(commandSlotOverrideInputSchema).max(ARRANGEMENT_LIMITS.SLOT_OVERRIDES).optional(),
    /**
     * ⚠️ [C6a] THE SECOND WAY TO FILL A ROLE — ASKED OF A DIVISION, ONE ROW PER POSITION.
     *
     * Absent or `[]` is BY NAME, which stays the default: every order stored before C6a reads exactly
     * as it did and re-saves exactly as it did. `commandSeat.schema.ts` records why a position is a
     * row rather than a quantity. Bounded by the same per-role limit as `officerIds`, because it is
     * the same question — how many people may this row seat.
     *
     * ⚠️ OPTIONAL, NEVER `.default([])` — `dutyPointInputSchema`'s rule: the standing-order generator
     * omits command entirely, and a default would be invisible to `updateSchema.test.ts`.
     */
    asks: z.array(commandAskInputSchema).max(ARRANGEMENT_LIMITS.COMMAND_OFFICERS_PER_ROLE).optional(),
  })
  .refine((entry) => exactlyOneOf(entry.roleKey, entry.adHocLabel), {
    message: 'A command row names exactly one policy role or one-off label',
  })
  /*
   * ⚠️ [C6a] THE TWO WAYS ARE EXCLUSIVE, AND THESE REFINES REFUSE ONLY THE NEW COMBINATION.
   *
   * "Neither" stays legal — a role with nobody named yet is an ordinary stored state, and so is a
   * role whose officers were cleared while its per-shift rows remained (`setOfficers` spreads the
   * old entry). Refusing either would make an existing order unsavable on its next autosave, which
   * is the one thing C6a promised not to do. What cannot exist is a role that is BOTH named and
   * asked, or a per-shift mapping on a role nobody names.
   */
  .refine((entry) => entry.officerIds[0] === undefined || entry.asks?.[0] === undefined, {
    path: ['asks'],
    message: 'A command role is either named or asked of a division, not both',
  })
  .refine((entry) => entry.perSlot?.[0] === undefined || entry.asks?.[0] === undefined, {
    path: ['perSlot'],
    message: 'A role asked of a division has no per-shift officers to map',
  })
  /*
   * ⚠️ A ONE-OFF LABEL MUST CONTAIN SOMETHING TO SLUG, exactly as a one-off COLUMN must.
   * `commandKeyOf` degrades to `'A:'` for a label with no letters or digits, so `'!!!'` and
   * `'???'` would be two rows with one identity and the duplicate check below would report
   * them as a duplicate — a true refusal with a confusing sentence.
   */
  .refine((entry) => !entry.adHocLabel || commandKeyOf(entry) !== 'A:', {
    path: ['adHocLabel'],
    message: 'A one-off command role name needs at least one letter or number',
  });

/**
 * ⚠️ [amended D4.3] TWO COMMAND ROWS ON ONE DUTY POINT MAY NOT SHARE AN IDENTITY.
 *
 * The same rule as `requirementLinesInputSchema`'s, one vocabulary over. Without it two rows
 * reading `Bomb squad` and `BOMB  SQUAD` both slug to `A:bomb_squad`, and `perSlot` — which
 * keys on the row — would silently re-point one card's per-shift override at the other's.
 *
 * ⚠️ NOT WIDENED ACROSS DUTY POINTS, for `dutyPointsInputSchema`'s reason: two cards may
 * legitimately name the same ad-hoc role, and their duties never meet.
 */
export const commandEntriesInputSchema = z
  .array(commandEntryInputSchema)
  .max(ARRANGEMENT_LIMITS.COMMAND_ENTRIES)
  .superRefine((rows, ctx) => {
    const seen = new Map<string, number>();
    rows.forEach((row, index) => {
      const key = commandKeyOf(row);
      const first = seen.get(key);
      if (first === undefined) seen.set(key, index);
      else {
        ctx.addIssue({
          code: 'custom',
          path: [index],
          message: `This command role is already listed at position ${String(first + 1)}.`,
        });
      }
    });
    /*
     * ⚠️ [C6a] AN ASKED POSITION'S ID IS UNIQUE ON ITS DUTY POINT — the seat it becomes is keyed on
     * `(arrangement, duty point, ask id)`. A web card copy re-mints them; a repeat here is a client
     * that did not, and it is refused rather than silently re-minted, because a re-mint is a new
     * identity nobody asked for.
     */
    const asked = new Set<string>();
    rows.forEach((row, index) => {
      (row.asks ?? []).forEach((ask, position) => {
        if (!ask.askId) return;
        if (asked.has(ask.askId)) {
          ctx.addIssue({ code: 'custom', path: [index, 'asks', position, 'askId'], message: 'Duplicate ask id' });
        }
        asked.add(ask.askId);
      });
    });
  });

export const commandEntrySchema = z.object({
  /** ⚠️ [amended D4.3] `null` on a one-off row, which names itself instead. */
  roleKey: z.string().nullable(),
  /** ⚠️ [amended D4.3] The one-off role's own name. `null` when `roleKey` names a policy role. */
  adHocLabel: z.string().nullable(),
  /**
   * Resolved from the frozen policy version, for display only — or, for a one-off row,
   * the row's OWN label, so every reader has one field to render and none has to branch.
   */
  roleLabel: z.string().nullable(),
  officerIds: z.array(objectIdSchema),
  /** Resolved for display, in the order the operator named them. */
  officers: z.array(officerSummarySchema),
  perSlot: z.array(
    z.object({ slotKey: z.string(), mode: z.enum(COMMAND_SLOT_MODES), officerIds: z.array(objectIdSchema) }),
  ),
  /**
   * ⚠️ [C6a] PRESENT ONLY WHEN THE ROLE IS ASKED OF A DIVISION — omitted, never `[]`, so an order
   * filled by name serialises byte for byte as it did before C6a (`commandSeats.test.ts` pins the
   * exact key set of a by-name entry). Each position carries its seat once the order is issued.
   */
  asks: z.array(commandAskSchema).optional(),
});

// ── Extras ───────────────────────────────────────────────────────────────────────

/**
 * FR-5.2.9 stores instructions as `{type, text}` objects from day one, so a later move to
 * block JSON is additive rather than a migration.
 */
export const instructionInputSchema = z.object({
  type: z.literal('LINE').default('LINE'),
  text: trimmedText(500),
});

export const supportingArrangementInputSchema = z.object({
  personName: trimmedText(120),
  responsibilities: z.array(trimmedText(300)).max(ARRANGEMENT_LIMITS.RESPONSIBILITIES),
});

// ── Slot overrides ───────────────────────────────────────────────────────────────

/**
 * "This shift is different" — the pre-publish half of detaching.
 *
 * ⚠️ NOT A TOGGLE OUT OF "SAME FOR ALL". Six identical shifts and one odd one must never
 * mean filling seven, so a detach writes ONE row here and every other slot keeps reading
 * the template. Publish looks each slot up in this map and sets `isDetached` on the
 * matching deployment; a re-stamp then skips exactly those.
 */
export const slotOverrideInputSchema = z.object({
  slotKey: z.string().trim().min(1).max(40),
  requirementLines: requirementLinesInputSchema,
});

// ── Duty points ──────────────────────────────────────────────────────────────────

/**
 * ONE DUTY POINT — a place (or an ordered route), when it runs, which policy governs it,
 * how much force it asks for and who commands it. [amended D4]
 *
 * It is everything the arrangement used to hold at its root, and the arrangement now holds
 * a list of them. `dutyPoint × slot → Deployment` replaces `arrangement × location(s) ×
 * slot`, so the atomic unit does not move and P4's fill layer sits below this change.
 */
export const dutyPointInputSchema = z.object({
  /**
   * ⚠️ THE STABLE IDENTITY, AND `deployments` KEY ON IT.
   *
   * Absent means "new, mint me one"; present means "this is the duty point you already
   * know" — exactly `shiftInputSchema.id`'s rule, and for exactly its reason. A POSITIONAL
   * key silently re-points every duty the moment a point is inserted above another, which
   * is `fillRef.util.ts`'s recorded lesson about `allocation._id`: it was advertised as
   * stable and was not, because `applyArrangementContent` rebuilds the array on every
   * PATCH. A minted string survives that; a sub-document `_id` does not.
   */
  id: z.string().trim().min(1).max(24).optional(),
  /** What the operator calls this point, if the place name is not enough. */
  name: z.string().trim().max(120).nullish(),
  /**
   * ⚠️ ONE PLACE IS A POINT; MANY ARE AN ORDERED ROUTE, WALKED IN THIS ORDER.
   *
   * §1c: separate places at once are separate DUTY POINTS, which is the concurrent case.
   * A route is one troop visiting several places in turn, which is what preserves P5's
   * Mark Reached per row — `deployment.locations.length` events, in order.
   */
  places: z.array(dutyPointPlaceInputSchema).max(ARRANGEMENT_LIMITS.PLACES_PER_DUTY_POINT),
  /**
   * ⚠️ NOT A MEMBER OF `places`, AND THE DIFFERENCE IS LOAD-BEARING. Where the force
   * assembles is not a stop on the route: as a member it would join `locationKey`, count
   * toward the duty's Mark Reached rows, and be refused as a duplicate in the common case
   * where the reporting place IS the first stop.
   */
  reportingPlace: dutyPointPlaceInputSchema.nullish(),
  /**
   * ⚠️ [amended C1c §1] WHEN THE FORCE IS TO ASSEMBLE — `HH:mm`, THE SAME TIME MODEL AS A SHIFT.
   *
   * §1b: "interpreted as a wall-clock time on the duty date, IST — the same way shift times
   * already are. Do not invent a second time model." A shift's `startTime` is a zero-padded
   * 24-hour string on this very schema, resolved to an instant by `generateSlots` at publish;
   * this is the same string resolved by the same `applyTime` on the same day. `HH:MM AM/PM` is
   * how it is ENTERED and DISPLAYED — `TimeField` already reads `5:00 PM` and emits `17:00`,
   * and `toTwelveHour` renders it back — and storing the 12-hour form would break the string
   * comparison `slot.util.ts` decides midnight-spanning with.
   *
   * ⚠️ INDEPENDENT OF `reportingPlace` (§1d). Either may be set without the other, so this is
   * its own nullish field rather than a member of the place object — "assemble at 07:00" is a
   * complete instruction when everybody already knows where.
   */
  reportingTime: timeOfDaySchema.nullish(),
  /**
   * [STAND-DOWN] WHEN A WHOLE-DAY DUTY STANDS DOWN — `HH:mm`, the same time model as `reportingTime`.
   *
   * Optional, and meaningful only on a `NO_FIXED_TIME` schedule: a shift already states its end,
   * so the service stores `null` for a shift-bearing card whatever is sent. With a reporting time
   * it reads as that duty's shift window (`3:00 PM–9:00 PM`); a stand-down at or before the
   * reporting time is the next morning (`standDownRollsOver`).
   *
   * ⚠️ IT DOES NOT MOVE `starts_at`/`ends_at`. A whole-day duty's instants still span its whole IST
   * date, so the naming, swap, marking and attendance windows are unchanged — this is the stated
   * END, stamped beside `reporting_at`, for every surface that shows the duty's time.
   */
  standDownTime: timeOfDaySchema.nullish(),
  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C4a §4] THE ZONE — the field [C2b §8f] deliberately did not guess at. Its entity is now
   *  settled: it belongs to the DUTY POINT.
   *
   *  ⚠️ A PLAIN STRING, STORED SO IT CAN BE FILTERED LATER — and NOTHING filters it yet. No zone
   *  master, no index, no list filter: §7 ships an index with the query that needs one. The builder
   *  autocompletes from the OTHER duty points of the same arrangement, which it already holds.
   *
   *  ⚠️ OPTIONAL. Blank is `null` on the record (`flattenDutyPoint`), and a card with no zone renders
   *  no zone anywhere. `.nullish()`, never `.default()` — this schema is `.partial()`-ed.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  zone: z.string().trim().max(ARRANGEMENT_LIMITS.ZONE).nullish(),
  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C5c §4] THE SECTOR — the zone's twin in every respect but ONE: IT HAS NO LENGTH LIMIT.
   *
   *  Free text beside the zone on the duty point, `null` when blank, filtered by nothing yet — the
   *  zone's paragraph above applies to it word for word. The difference is the client's own
   *  instruction, "no length limit", so there is no `.max` here and no `SECTOR` key in
   *  `ARRANGEMENT_LIMITS` — see the comment there for what bounds it instead.
   *
   *  ⚠️ `.nullish()`, never `.default()` — this schema is `.partial()`-ed.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  sector: z.string().trim().nullish(),
  /**
   * ⚠️ OPTIONAL, NOT DEFAULTED, AND THE REASON MOVED HERE WITH IT.
   *
   * A literal default would have to name a date, and the only sensible one is the
   * arrangement's `paDate` — which a sibling default cannot see. The service anchors an
   * absent schedule on `paDate` as a one-day, no-fixed-time schedule, where it CAN see it.
   * A default of `date: ''` would be a value this very schema rejects.
   */
  schedule: scheduleInputSchema.optional(),
  /**
   * ⚠️ THE POLICY IS PER DUTY POINT, AND THE VERSION IS WHAT IS FROZEN.
   *
   * Absent means "use this policy's current published version", which the service resolves
   * — the client cannot be the authority on which version is current. [FIX-1] therefore
   * holds per duty point: an issued order's columns are resolved from the version it names,
   * archived master rows and all.
   */
  policyId: objectIdSchema.optional(),
  policyVersionId: objectIdSchema.optional(),
  requirementLines: requirementLinesInputSchema,
  /*
   * ⚠️ OPTIONAL, NOT DEFAULTED, AND NOT MERELY FOR CONVENIENCE.
   *
   * `standingOrderGenerator.service.ts` must be able to write a duty point WITHOUT NAMING
   * COMMAND AT ALL — its own header states that the absence is the mechanism keeping it off
   * `commandRoleIsolation.test.ts` CLAIM 5's allow-list, and round-the-clock captures no
   * supervision by design. A required key would force it to write `commandEntries: []`,
   * which is a mention, which widens the guard.
   *
   * ⚠️ AND `.optional()` RATHER THAN `.default([])`: a default inside a `.partial()`-ed
   * base does not get stripped (CLAUDE.md §6), and `updateSchema.test.ts` is structurally
   * blind to a nested one. Absent means "the model's own `default: []`", which is where the
   * empty array belongs.
   */
  commandEntries: commandEntriesInputSchema.optional(),
  slotOverrides: z.array(slotOverrideInputSchema).max(ARRANGEMENT_LIMITS.SLOT_OVERRIDES).optional(),
  /**
   * ⚠️ [amended D4.3] POINT-SPECIFIC INSTRUCTIONS - the standing orders for THIS place.
   *
   * This REOPENS OD-20, which closed as "PA-level only, no per-deployment addendum", and the
   * duty point is what changed: an order covering three places at three times has three sets
   * of standing orders, and folding them into one PA-level list makes the operator write
   * "at Esplanade:" in front of every line.
   *
   * ⚠️ THE ARRANGEMENT-LEVEL PAIR STAYS EXACTLY WHERE IT IS. These are ADDITIONAL, and there
   * is deliberately NO discriminator column collapsing the two into one array - "general" and
   * "for this place" are two questions with two homes, and a single field with a kind flag
   * would make every reader filter before it could render either.
   *
   * ⚠️ `.optional()` AND NEVER `.default([])`, exactly like `commandEntries` above: `.partial()`
   * does not recurse, so `updateSchema.test.ts` is structurally blind to a default nested here
   * and one would fire on every PATCH carrying a duty point. Absent means the model's own
   * `default: []`, which is where an empty array belongs.
   */
  instructions: z.array(instructionInputSchema).max(ARRANGEMENT_LIMITS.INSTRUCTIONS_PER_DUTY_POINT).optional(),
  supportingArrangements: z
    .array(supportingArrangementInputSchema)
    .max(ARRANGEMENT_LIMITS.SUPPORTING_PER_DUTY_POINT)
    .optional(),
});
/*
 * ⚠️ NO `.default()` ANYWHERE INSIDE `dutyPointInputSchema`, AND THE USUAL GUARD IS BLIND
 * TO IT. `.partial()` operates on TOP-LEVEL keys and does not recurse, so
 * `updateSchema.test.ts` cannot see a default nested in here — and one would still fire on
 * every PATCH carrying a duty point. That test gains an explicit case for this array.
 */

/**
 * ⚠️ TWO DUTY POINTS MAY NOT SHARE AN IDENTITY.
 *
 * A deployment is keyed `(arrangement, duty point, slot)`, so two points answering to one
 * id would collide on the unique index at publish — an E11000 with a Mongo index name in
 * it, where the operator's real mistake is a duplicated card they can see and remove. Same
 * argument, same shape, as `requirementLinesInputSchema`'s below it.
 *
 * ⚠️ AND THE PER-LINE INVARIANT IS DELIBERATELY *NOT* WIDENED ACROSS DUTY POINTS.
 * `fillRef.util.ts`'s `slotRef` lives on a DEPLOYMENT, and a deployment now belongs to
 * exactly one duty point — so two points may both ask for "10 UB Men from Central" and
 * their slot refs never meet. Widening it would forbid the single most common real
 * arrangement: the same force at two places.
 */
export const dutyPointsInputSchema = z
  .array(dutyPointInputSchema)
  .max(ARRANGEMENT_LIMITS.DUTY_POINTS)
  .superRefine((points, ctx) => {
    const seen = new Map<string, number>();
    points.forEach((point, index) => {
      if (point.id === undefined) return;
      const first = seen.get(point.id);
      if (first === undefined) seen.set(point.id, index);
      else {
        ctx.addIssue({
          code: 'custom',
          path: [index, 'id'],
          message: `This duty point is already listed at position ${String(first + 1)}.`,
        });
      }
    });
  });

// ── Shared columns ───────────────────────────────────────────────────────────────

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C5c §2] ONE PERSON, MANY DUTY POINTS — A COLUMN ASKED FOR ONCE, ON ITS ANCHOR CARD.
 *
 *  An inspector supervising five duty points was named five times, because every total in this
 *  system is a plain sum over cards and duties (premise a). The only way a column is counted
 *  ONCE is for it to exist once: the ANCHOR card carries the requirement line, and every other
 *  card carries NO line for it at all. Every counting reader — the publish personnel figure, the
 *  preview's `required`, the fill roll-up, the dashboard tiles — is therefore unchanged, and none
 *  of them reads this.
 *
 *  ⚠️ THE ANCHOR IS A `duty_point_id`, NEVER A POSITION (§2g). Dragging the stack rebuilds the
 *  array by id, so the anchor cannot move; deleting the anchor ENDS sharing for its columns and
 *  the builder restores every other card's own numbers from the stash — it is never re-homed.
 *
 *  ⚠️ THE STASH IS THE OTHER CARDS' OWN NUMBERS, AND NOTHING COUNTS IT. Switching sharing off
 *  (§2e) puts each card's column back as it was; a card added after sharing began has no stash
 *  row and comes back at zero. It is inert data a counting reader must never read.
 *
 *  ⚠️ DECIDED ON A DRAFT AND FROZEN AT PUBLISH (U1). Changing which columns are shared on an
 *  issued order is a 409 stating how many officers are named on it.
 *
 *  ⚠️ NO `.default()` ANYWHERE INSIDE, for `dutyPointInputSchema`'s reason: this is `.partial()`-ed
 *  into the update schema, and a nested default would fire on every PATCH that carries it.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const sharedColumnStashInputSchema = z.object({
  /** The covered card whose own numbers these are. */
  dutyPointId: z.string().trim().min(1).max(24),
  requiredCount: z.number().int().min(0).max(99_999),
  /** Where the line sat on its card, so switching sharing off puts it back in place. */
  displayOrder: z.number().int().min(0),
  restrictToPostTypeIds: z.array(objectIdSchema).max(ARRANGEMENT_LIMITS.ALLOCATIONS_PER_LINE).optional(),
  allocations: z.array(allocationInputSchema).max(ARRANGEMENT_LIMITS.ALLOCATIONS_PER_LINE),
});

export const sharedColumnInputSchema = z.object({
  /** `lineKeyOf`'s own key — `P:`, `V:` or `A:<slug>` — never a hand-built one. */
  lineKey: z.string().regex(LINE_KEY_REGEX, 'Not a column this order can share'),
  anchorDutyPointId: z.string().trim().min(1).max(24),
  stashed: z.array(sharedColumnStashInputSchema).max(ARRANGEMENT_LIMITS.DUTY_POINTS),
});

/**
 * ⚠️ ONE ENTRY PER COLUMN, AND ONE STASH ROW PER CARD WITHIN IT — `requirementLinesInputSchema`'s
 * idiom. The cross-card rules (the anchor exists and carries the line, no other card does, every
 * card binds one version) need the duty points and the resolved versions, so they are asserted by
 * `applyArrangementContent` on the document it has just written, where every writer passes.
 */
export const sharedColumnsInputSchema = z
  .array(sharedColumnInputSchema)
  .max(ARRANGEMENT_LIMITS.SHARED_COLUMNS)
  .superRefine((entries, ctx) => {
    const seen = new Map<string, number>();
    entries.forEach((entry, index) => {
      const first = seen.get(entry.lineKey);
      if (first === undefined) seen.set(entry.lineKey, index);
      else {
        ctx.addIssue({
          code: 'custom',
          path: [index, 'lineKey'],
          message: `This column is already shared at position ${String(first + 1)}.`,
        });
      }

      const cards = new Set<string>();
      entry.stashed.forEach((row, rowIndex) => {
        if (cards.has(row.dutyPointId)) {
          ctx.addIssue({
            code: 'custom',
            path: [index, 'stashed', rowIndex, 'dutyPointId'],
            message: 'A duty point can set aside one number for a shared column, not two.',
          });
        }
        cards.add(row.dutyPointId);

        const sources = new Set<string>();
        row.allocations.forEach((allocation, allocationIndex) => {
          const key = allocKeyOf(allocation);
          if (sources.has(key)) {
            ctx.addIssue({
              code: 'custom',
              path: [index, 'stashed', rowIndex, 'allocations', allocationIndex],
              message: 'This source is already listed on this column. Put the whole number on one row.',
            });
          }
          sources.add(key);
        });
      });
    });
  });

// ── The arrangement ──────────────────────────────────────────────────────────────

/**
 * ⚠️ NO `.default()` ANYWHERE IN THIS BASE (CLAUDE.md §6 [amended P2]).
 *
 * In zod 4 `.partial()` does not strip a default — it yields `ZodOptional<ZodDefault<…>>`
 * and the default still fires on a missing key, which silently defeats the non-empty
 * refine every update schema relies on. It shipped twice before. Defaults go on
 * `createArrangementSchema` via `.extend`, and `packages/shared/src/schema/updateSchema.test.ts`
 * asserts this over the whole barrel the day `updateArrangementSchema` is exported.
 */
export const arrangementBaseSchema = z.object({
  /**
   * ⚠️ [amended D4] PURELY ADMINISTRATIVE — the date printed on the order, and the date an
   * arrangement GROUP keys on. It is NOT a duty date and answers nothing about the
   * schedules beneath it: one order can be dated the 15th and hold a duty point that runs
   * only on the 17th. The operational question is answered by `firstDutyDate`/`lastDutyDate`
   * on the output, which are DERIVED and never typed.
   */
  paDate: calendarDateSchema,
  /** The department's own number, if they use one. Free text — never parsed. */
  paNumber: z.string().trim().max(20).nullish(),
  /** ⚠️ NULL means "derive it". See `arrangementTitle.util.ts`. */
  title: z.string().trim().max(200).nullish(),
  description: z.string().trim().max(2_000).nullish(),
  /**
   * ⚠️ [amended D4] FREE TEXT, NEVER PARSED AND NEVER COUNTED — `paNumber`'s precedent.
   *
   * "about 20,000", "2 lakh" and "unknown" are all real answers an operator will give, and
   * none of them is a number. Storing one would also put an integer next to the force
   * totals, and the one thing this file will not do is invite arithmetic between a crowd
   * estimate and a requirement.
   */
  estimatedCrowdSize: z.string().trim().max(60).nullish(),
  dutyPoints: dutyPointsInputSchema,
  instructions: z.array(instructionInputSchema).max(ARRANGEMENT_LIMITS.INSTRUCTIONS),
  supportingArrangements: z.array(supportingArrangementInputSchema).max(ARRANGEMENT_LIMITS.SUPPORTING),
  /**
   * ─── ⚠️ [C1b §4] ASSOCIATED UNITS — VISIBILITY ONLY, AND NEVER A FILL TASK ───────
   *
   * Units the creator names so they can SEE this order in full. They may belong to any
   * division, including one with no allocation on it — FR-3.6's isolation is otherwise
   * absolute, and this is the one per-arrangement, deliberately-chosen exception to it.
   *
   * ⚠️ **AN ASSOCIATION MANUFACTURES NO WORK.** The unit appears in To Assign only if a
   * requirement line names it, exactly as before. Confirmed with the client.
   *
   * ⚠️ **AND NOTE THE `.default()` THAT IS NOT HERE.** This base is `.partial()`-ed into the
   * update schema, and in zod 4 `.partial()` does NOT strip a default — it yields
   * `ZodOptional<ZodDefault<…>>` and the default still fires on a missing key. A
   * `.default([])` on this line would make `PATCH {title: 'x'}` parse to
   * `{title, associatedOrgUnitIds: []}` and WIPE EVERY ASSOCIATION ON EVERY KEYSTROKE of the
   * title field, on an autosaving form. That is `placeBaseSchema.aliases`' recorded bug
   * arriving through the same door; `updateSchema.test.ts` is the standing guard.
   */
  associatedOrgUnitIds: z.array(objectIdSchema).max(ARRANGEMENT_LIMITS.ASSOCIATED_UNITS),
  /**
   * ⚠️ [C5c §2] WHICH COLUMNS ARE ASKED FOR ONCE ACROSS EVERY DUTY POINT — see the block above.
   * Required on the base and defaulted ONLY on create, `associatedOrgUnitIds`' own rule: a
   * `.default([])` here would make every title-only PATCH end sharing and discard its stash.
   */
  sharedColumns: sharedColumnsInputSchema,
});

/**
 * ⚠️ A DRAFT MAY BE ALMOST EMPTY, AND THAT IS REQUIRED RATHER THAN TOLERATED.
 *
 * Autosave fires from the first keystroke (FR-5.2.1), so the very first write happens when
 * the operator has typed an order date and nothing else. Everything a complete arrangement
 * needs — a duty point, a place, a balanced grid, a required command role — is a
 * STATE-TRANSITION rule checked at publish, not a shape rule checked here.
 *
 * ⚠️ [amended D4] AND `policyId` IS NO LONGER AMONG THEM. The policy moved onto the duty
 * point, and tab 1 of the intake asks for no policy at all — so an arrangement with no
 * duty point and therefore no policy is the legitimate first save.
 */
export const createArrangementSchema = arrangementBaseSchema.partial().extend({
  paDate: calendarDateSchema,
  dutyPoints: arrangementBaseSchema.shape.dutyPoints.default([]),
  instructions: arrangementBaseSchema.shape.instructions.default([]),
  supportingArrangements: arrangementBaseSchema.shape.supportingArrangements.default([]),
  associatedOrgUnitIds: arrangementBaseSchema.shape.associatedOrgUnitIds.default([]),
  sharedColumns: arrangementBaseSchema.shape.sharedColumns.default([]),
});

export const updateArrangementSchema = arrangementBaseSchema
  .partial()
  .refine((value) => Object.keys(value).length > 0, { message: 'At least one field must be provided' });

// ── Output ───────────────────────────────────────────────────────────────────────

/**
 * One duty point, as a reader gets it back. [amended D4]
 *
 * ⚠️ THE COUNTS ARE PER POINT AND THE ARRANGEMENT'S ARE SUMS OF THEM. A reader who wants
 * "how many duties will this card make" asks the card; nothing has to re-derive it.
 */
/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C1e §2] THE QUICK PREVIEW — A SECOND VIEW OF ONE ENDPOINT, NEVER A SECOND SHAPE.
 *
 *  [P5b]'s rule, and this is its second instance. `GET /arrangements/:id?view=preview`
 *  returns the SAME `arrangementSchema`, with three keys filled in that `view=record`
 *  omits entirely. There is no discriminated union to parse and no second DTO to keep in
 *  step — the dashboard's detail page and the preview modal render one object.
 *
 *  ⚠️ EVERY ADDED KEY IS `.optional()`, SO THE DEFAULT VIEW DOES NOT MOVE A BYTE.
 *  `JSON.stringify` omits `undefined`, which is [P6d]'s measured `dutyDateResolved`
 *  argument verbatim. The identity is also STRUCTURAL rather than promised: the service
 *  returns `toDto`'s value unmodified on the default path, so there is no branch inside
 *  `toDto` to get wrong — and `arrangement.test.ts` pins the default view's exact root key
 *  set, which nothing did before.
 *
 *  ⚠️ AND THERE IS NO `rostersIncluded` BOOLEAN. [P5b] needed one because its arrays were
 *  REQUIRED, so `force: []` was ambiguous with "nobody named yet". Here the whole block is
 *  optional, so its ABSENCE already says the preview did not run — and a key that is always
 *  `true` when present answers nothing.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [amended C1f §2a] NO OFFICER NAMES AT ALL. A COUNT TABLE, DIVISION-WISE.
 *
 *  [C1e] carried up to `PREVIEW_ROSTER_NAMES` names per column and derived a `+N`. Both are
 *  DELETED, along with `previewRosterNameSchema` and that constant — §9's no-dead-tooling
 *  rule, and none of them has another reader.
 *
 *  ⚠️ THE QUESTION A PREVIEW ANSWERS IS WHETHER THE ORDER IS STAFFED, NOT WHO IS ON IT.
 *  Confirmed with the client. Who was named is the fill screen's question and the order
 *  sheet's; a glance wants the SHORTFALL and where it sits.
 *
 *  ⚠️ AND THE ABSENCE IS STRUCTURAL RATHER THAN A PROJECTION SOMEBODY MUST REMEMBER.
 *  `arrangementPreview.service.ts` no longer reads the officer master at all — it does not
 *  import `Officer`, and `arrangementPreview.test.ts` scans the source to keep it that way.
 *  `loadAppForceOfficers`' rule taken one step further: there is no point in the request at
 *  which an officer's name or number exists.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
/**
 * ⚠️ [amended C1h §4h] ONE ROW PER **ADDRESSEE**, NOT PER DIVISION — AND §4h COULD NOT BE BUILT
 * WITHOUT IT.
 *
 * The brief: "EXPECT PREVIEW AND BUILDER TO DIVERGE FOR A DELEGATED ORDER — [C1f] splits an
 * allocation line, so `CD · Any` becomes two rows. That is correct and matches the paper; do not
 * reconcile it." With a division-only key those two rows COLLAPSE into one "Central Division",
 * which is the opposite of what §4h describes — so the unit joins the payload.
 *
 * ⚠️ IT REVERSES THIS SCHEMA'S OWN [C1e §2a] NOTE, which read "a unit-addressed allocation rolls
 * up to its division… the cost, stated: a delegated share's own progress is invisible at this
 * altitude." That was the right trade for a LIST of names; it is the wrong one for a MATRIX,
 * whose whole subject is which addressee owes what.
 *
 * ⚠️ AND THE ADDITIONS ARE ADDITIVE. `divisionOrgUnitId` and `label` keep their meaning exactly,
 * so `arrangementPreview.test.ts`'s pinned ROOT key set is untouched and a reader that only wants
 * the division still gets the division.
 */
export const previewDivisionRowSchema = z.object({
  /**
   * ⚠️ `<division>:<unit|*>` — `fillRef.util.ts`'s OWN key, so the matrix's side and the fill
   * screen's rows speak one vocabulary rather than two derivations of it. It is also the stable
   * React key for a row, which a division id stopped being the moment two rows could share one.
   */
  allocKey: z.string(),
  /** The stored `allocations[].division_org_unit_id`. [P4]'s `divisionOfAllocKey` is the seam. */
  divisionOrgUnitId: objectIdSchema,
  /**
   * Resolved server-side — `previewRosterRowSchema`'s own `lineKey` + `label` pair, one level
   * down. Deliberately NOT `orgUnitSummarySchema`: an allocation row carries the full summary
   * because its screen branches on `type`/`subType`, and a preview renders one string.
   */
  label: z.string(),
  /**
   * The addressed UNIT, where the line names one. `null` for a division-wide row, which is the
   * ordinary case and the fast path.
   *
   * ⚠️ REQUIRED-KEY-NULLABLE, NOT OPTIONAL — §6's own test: the matrix renders a labelled cell
   * for this row either way, so `null` answers a question the screen is asking. [P6d]'s optional
   * form is for a key whose PRESENCE is the signal that a different code path ran.
   */
  unitOrgUnitId: objectIdSchema.nullable(),
  unitLabel: z.string().nullable(),
  requiredCount: z.number().int().min(0),
  namedCount: z.number().int().min(0),
});

/** One post/vehicle/one-off column of one duty, summarised. */
export const previewRosterRowSchema = z.object({
  /** [P4]'s identity, so a row joins back to its `requirementLines` entry. */
  lineKey: z.string(),
  /**
   * ⚠️ [amended C3a §5] WHAT THE COLUMN IS, BECAUSE A VEHICLE IS A **COUNT** AND NOT A ROSTER.
   *
   * [C2a §5a] removed the officer treatment from a vehicle line on the fill screen — no slots,
   * no chips, no `StatusDot` — because red-amber-green answers "is this staffed", which a van
   * cannot be. §5's own move of the count into the post heading put `(0 of 2 named)` back on a
   * van, on two screens, because this row had no way to say which it was.
   *
   * ⚠️ IT IS A FIELD RATHER THAN A PREFIX TEST ON `lineKey`. That prefix is how `fillRef.util.ts`
   * MINTS a key; this is what the line IS, and parsing a mint format is the
   * identity-from-a-position family §7 has corrected five times. `fillLineSchema.kind` has
   * carried exactly this vocabulary since [P4].
   */
  kind: z.enum(['post', 'vehicle', 'adhoc']),
  label: z.string(),
  requiredCount: z.number().int().min(0),
  namedCount: z.number().int().min(0),
  /**
   * ⚠️ A REQUIRED KEY HOLDING A POSSIBLY-EMPTY ARRAY — never optional, never nullable.
   *
   * §6's test is answered one level up: `dutyPointSchema.preview` is ALREADY optional, and
   * that single optional is what says "the preview did not run". A second one inside it would
   * answer a question nothing asks. `[]` is a statement — "this column is addressed to
   * nobody" — which a DRAFT legitimately holds and `previewPublish` blocks at issue.
   *
   * ⚠️ THE NAMED COUNTS SUM TO `namedCount`; THE REQUIRED COUNTS NEED NOT SUM TO
   * `requiredCount`. `requiredCount` is the column's own `required_count`, and these sum the
   * ALLOCATIONS — which `previewPublish` already warns can be short of it or over it. A test
   * asserting the second equality would go red on an ordinary under-allocated order.
   */
  divisions: z.array(previewDivisionRowSchema),
});

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C4a §7] A SHORT-FALL, AS THE ORDER STATES IT — one division or unit that closed a duty short.
 *
 *  [C3c.2] built `Complete With Short-Fall` and stored `allocation_fills.shortfall_reason`; nothing
 *  rendered it. This is the preview's and the printed order's shape for it, built by ONE server
 *  function (`shortfallNotesFor`) so the two surfaces cannot state different numbers.
 *
 *  ⚠️ `askedCount` IS STAFFABLE SLOTS ONLY. A share's stored `allocated_count` included VEHICLE
 *  slots, which no officer can ever fill — so "asked for 12, sent 10" over a duty with two vans
 *  would print a shortage of two people that is really two vans. `isStaffableLine` excludes them.
 *  ⚠️ [amended P7A §3] It no longer includes them — the writer counts people (R1, fixed) — and the
 *  rule stays: a vehicle is never short, on any surface.
 *
 *  ⚠️ `sentCount` IS LIVE, and a note exists only while `shortCount >= 1`. A name added after the
 *  division closed short raises it, and a share since filled produces no note, so paper never
 *  states a shortage that has gone. `.min(1)` on both counts says so in the contract.
 *
 *  ⚠️ `reason` IS THE DIVISION'S OWN WORDS (§7c) — capturing it and not printing it would make the
 *  capture ceremonial. It is readable by anyone who can open the order, which is what putting it on
 *  the order means.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C5 §7d] TWO KINDS OF GAP, AND A COMMANDER ACTS DIFFERENTLY ON EACH.
 *
 *   • `OPEN`     — 10 of 14 named. Nobody has said anything; the work is simply not done, and
 *                  somebody can still be chased. `reason` is `null`.
 *   • `DECLARED` — the division closed its share short under [C3c.2] and gave a reason. It is a
 *                  STATEMENT that no more are coming, and it is final: `completed_at` has no
 *                  un-setter anywhere in the product.
 *
 *  ⚠️ COLLAPSING THEM WOULD BE THE SAME MISTAKE §3 IS ABOUT, one grain down. "2 short" reads as
 *  a to-do list; "2 short, no reserve available" reads as an answer. A screen that shows them
 *  alike tells a commander to chase a division that has already replied.
 *
 *  ⚠️ THE DISCRIMINATOR IS `reason`, NEVER `completed_at`. That column is also written by
 *  `autoCompleteFullShares` with no reason at all — [C3c.2]'s rule, which `shortfallNotesFor`
 *  has followed since [C4a §7] and which §3 has now taught the roll-up as well.
 *
 *  ⚠️ `reason` IS NULLABLE RATHER THAN OPTIONAL, and `kind` carries the meaning. A missing key
 *  would make an older server indistinguishable from an open gap; §6's test is that every
 *  renderer prints something either way, so `null` answers the question the screen is asking.
 *
 *  ⚠️ [P7A §4] DUTY MITRA READS THE `DECLARED` KIND TOO — as `force[].sources[].closedShortReason`
 *  (`officerApp.schema.ts`), from this builder, so the phone and the paper cannot disagree about
 *  which division has replied.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const SHORTFALL_KINDS = ['OPEN', 'DECLARED'] as const;
export type ShortfallKind = (typeof SHORTFALL_KINDS)[number];

export const shortfallNoteSchema = z.object({
  /** The addressee that is short: the named unit, or the division. */
  addresseeOrgUnitId: objectIdSchema,
  /** `Division` or `Division · Unit`, resolved server-side — never a hex id. */
  label: z.string(),
  /** ⚠️ [C5 §7a] The allocation row this gap belongs to, so a sheet can print it IN the block. */
  allocKey: z.string(),
  kind: z.enum(SHORTFALL_KINDS),
  askedCount: z.number().int().min(1),
  sentCount: z.number().int().min(0),
  shortCount: z.number().int().min(1),
  /** ⚠️ [C5 §7d] The division's own words on a `DECLARED` note, and `null` on an `OPEN` one. */
  reason: z.string().min(1).max(300).nullable(),
});

/**
 * ⚠️ ONE DUTY PER DUTY POINT, AND THE **SERVER** CHOSE IT.
 *
 * A seventeen-day three-shift order is fifty-one duties; carrying a roster for each is the
 * payload `MAX_ORDER_SHEET_ASSIGNMENTS` exists to refuse. So the preview carries the
 * NEAREST UPCOMING duty of each point and its roster, and the point-wide counts beside it.
 *
 * ⚠️ THE SERVER RESOLVES IT AND ECHOES WHICH ONE IT PICKED — `dutyDateResolved`'s pattern.
 * A client re-deriving "nearest upcoming" would be a second expression of that rule, and the
 * two disagree at midnight, on the one screen whose whole job is a fast correct glance.
 */
export const previewDutySchema = z.object({
  deploymentId: objectIdSchema,
  dutyDate: calendarDateSchema,
  slotLabel: z.string(),
  timeLabel: z.string(),
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  requiredCount: z.number().int().min(0),
  namedCount: z.number().int().min(0),
  posts: z.array(previewRosterRowSchema),
  /** ⚠️ [C4a §7a] REQUIRED, possibly empty — `[]` is "nobody closed this duty short". */
  shortfalls: z.array(shortfallNoteSchema),
  /**
   * ⚠️ [C5c §5a] THIS DUTY'S `Present · Absent · Not arrived`, over the officers NAMED on it — the
   * opened duty's, because the notes follow the matrix they sit under.
   *
   * ⚠️ `null` UNTIL THE DUTY HAS STARTED, AND REQUIRED-NULLABLE rather than optional: every
   * renderer asks the question, and `null` answers it — §6d's "no line before duty starts".
   */
  presence: presenceTallySchema.nullable(),
});

export const dutyPointPreviewSchema = z.object({
  /** Point-wide, so a COLLAPSED card renders `N of M` without opening anything. */
  allocatedCount: z.number().int().min(0),
  namedCount: z.number().int().min(0),
  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C5 §8a] HOW MANY OFFICERS THIS CARD STILL LACKS — OBJECTIVE, AND IDENTICAL FOR EVERY
   *  VIEWER.
   *
   *  ⚠️ IT IS NOT `allocatedCount - namedCount`, AND THAT IS THE WHOLE REASON IT EXISTS.
   *  `allocated_count` included VEHICLE slots, which nobody can fill — `shortfallNotesFor`'s own
   *  header says "ASKED IS STAFFABLE SLOTS, NEVER `allocated_count`" and
   *  `notifyNamedOfficers` makes the same arithmetic — so the subtraction overstated the gap on
   *  any card with a van, permanently, by exactly the number of vans. A confidently wrong number
   *  on a policing screen is the failure this repository refuses.
   *  ⚠️ [amended P7A §3] The stored count no longer includes vans (the writer counts people, R1
   *  fixed); this figure is still computed from the requirement's people slots, so it never did.
   *
   *  ⚠️ POINT-WIDE, over every night of the card, which is `allocatedCount`'s own grain. §8b's
   *  personal figure sits beside it at the SAME grain deliberately: two numbers on one row
   *  answering different questions about different sets is worse than either alone.
   *
   *  ⚠️ AND IT IS SERVER-SIDE. Deriving it in the browser would need `planSlots` and
   *  `isStaffableLine` — `@pams/shared/utils`, which `pwa` cannot import — so a client-side
   *  answer would be a second expression that only one surface could hold.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  unfilledCount: z.number().int().min(0),
  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C5 §8b] WHAT **THIS VIEWER** STILL OWES ON THIS CARD — and `null` when that question
   *  has no answer for them.
   *
   *  ⚠️ IT IS A SECOND FIGURE, NEVER A MERGED ONE. [UX1 §3] settled — and this project has
   *  re-litigated three times — that a division which DELEGATED everything has discharged its
   *  obligation while the duty point remains unstaffed. One indicator lies to somebody: to the
   *  division it would say they still owe work they have handed on, and to a commander it would
   *  say the duty is covered. Two numbers say two true things.
   *
   *  ⚠️ `null` FOR A CALLER WITH NO SCOPE OF THEIR OWN — a `GLOBAL` scope, i.e. a super admin,
   *  for whom "yours" is not a question the data can answer. `0` would be a CLAIM ("you owe
   *  nothing"), which is `arrangementSchema.presence`'s own null-versus-zero rule.
   *
   *  ⚠️ AND A DIVISION THAT DELEGATED EVERYTHING READS `0` BY CONSTRUCTION, not by a rule.
   *  `delegateAllocation` writes the retained source row only when `plan.retained > 0`, so a
   *  fully delegated post leaves that division no share at all — and this is counted from
   *  `allocation_fills`, which is exactly the set of shares somebody was asked for.
   *
   *  ⚠️ [amended C5b §3] THAT LAST PARAGRAPH IS FALSE, AND IT IS RECORDED RATHER THAN FIXED (F8).
   *  The division has no share, but its STATIONS' shares carry the division among their
   *  ancestors in `visible_org_unit_ids`, and this is matched on the caller's root — so a
   *  division that delegated everything reads its stations' outstanding slots as its own.
   *  `docs/status-matrix.md` S10 states the figure and its register row R8 the reason.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  yoursUnfilled: z.number().int().min(0).nullable(),
  /**
   * ⚠️ `null` FOR A CARD WITH NO DUTY AT ALL — a DRAFT, or a point whose duties were all
   * detached. [P6b.2]: a panel that iterates a collection must ANSWER for the members with
   * nothing rather than skip them, because "no duty here" and "this screen does not show
   * that card" look identical when a card is simply missing.
   */
  duty: previewDutySchema.nullable(),
});

export const dutyPointSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  /** Derived from the places when `name` is null, so a card always has something to show. */
  displayTitle: z.string(),
  displayOrder: z.number().int().min(0),
  places: z.array(dutyPointPlaceSchema),
  reportingPlace: dutyPointPlaceSchema.nullable(),
  /**
   * ⚠️ [amended C1c §1] `HH:mm`, or `null`. Rendered 12-hour; never stored that way.
   * ⚠️ [amended C5b §3e] Always `null` on a shift-bearing card — its duties report at their own start
   * (`cardReportingTimeOf`), whatever a pre-C5b record still stores.
   */
  reportingTime: timeOfDaySchema.nullable(),
  /** [STAND-DOWN] `HH:mm`, or `null`. Always `null` on a shift-bearing card. */
  standDownTime: timeOfDaySchema.nullable(),
  /** [C4a §4] The card's zone, or `null`. Required key, nullable value — a card renders it or does not. */
  zone: z.string().nullable(),
  /** [C5c §4] The card's sector, or `null` — the zone's shape, and of any length. */
  sector: z.string().nullable(),
  schedule: scheduleInputSchema,
  policyId: objectIdSchema,
  policyVersionId: objectIdSchema,
  policyVersionNumber: z.number().int().min(1),
  policy: policySummarySchema.nullable(),
  requirementLines: z.array(requirementLineSchema),
  commandEntries: z.array(commandEntrySchema),
  slotOverrides: z.array(z.object({ slotKey: z.string(), requirementLines: z.array(requirementLineSchema) })),
  /** ⚠️ [amended D4.3] The point's OWN instructions. The arrangement's stay on the arrangement. */
  instructions: z.array(z.object({ type: z.literal('LINE'), text: z.string() })),
  supportingArrangements: z.array(z.object({ personName: z.string(), responsibilities: z.array(z.string()) })),
  placeCount: z.number().int().min(0),
  slotCount: z.number().int().min(0),
  dutyDayCount: z.number().int().min(0),
  deploymentCount: z.number().int().min(0),
  firstDutyDate: calendarDateSchema.nullable(),
  /** ⚠️ [C1e §2] Present only under `?view=preview`. See the block above `dutyPointSchema`. */
  preview: dutyPointPreviewSchema.optional(),
  lastDutyDate: calendarDateSchema.nullable(),
});

/**
 * ⚠️ [C1b §4] ONE ASSOCIATED UNIT, WITH ITS DIVISION BESIDE IT — AND THE DIVISION IS NOT
 * DECORATION.
 *
 * §4c lets an association cross a division boundary, and §1b says that crossing must be
 * VISIBLE AS SUCH rather than an accidental consequence of a widened query. A chip reading
 * `Alipore PS` tells an operator nothing about which division they have just handed sight of
 * this order to; `Alipore PS (ALIP) · South Division` does. `null` is a real answer — a unit
 * need not sit under a division at all (`loadDivisionForOrgUnit`).
 */
export const associatedUnitSchema = z.object({
  unit: orgUnitSummarySchema,
  division: orgUnitSummarySchema.nullable(),
});

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [amended C2b §4f] THE GROUP AN ORDER BELONGS TO, AND WHAT IT IS *WITHIN* IT.
 *
 *  §4d says counts are per-arrangement and are NEVER summed across a group — the parent reads
 *  its own 50, the corrigendum its own 12, and every dashboard, tile and preview keeps
 *  aggregating per arrangement. §4f is the whole compensation for that: **every screen showing
 *  either shows the link plainly.** So the relationship has to be on the payload of both, and
 *  this is it.
 *
 *  ⚠️ `role` IS WHAT *THIS* ORDER IS, not what the group is. A reader needs to know whether
 *  they are looking at the original or an amendment before they can read the numbers on the
 *  screen correctly, and `kind` alone cannot say.
 *
 *  ⚠️ `parent` IS SCOPE-FILTERED AND THEREFORE NULLABLE, exactly as `memberCount` is. A
 *  corrigendum and its parent can be addressed to different divisions (§4e), and FR-3.6 makes
 *  them mutually invisible — so returning the parent unconditionally would publish the
 *  existence of an order this caller may not see, which is the leak `memberCount` was already
 *  narrowed to avoid.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const arrangementGroupRefSchema = z.object({
  id: objectIdSchema,
  name: z.string(),
  kind: z.enum(ARRANGEMENT_GROUP_KINDS),
  memberCount: z.number().int().min(0),
  /** ⚠️ What THIS arrangement is within the group. `MEMBER` on a manual band. */
  role: z.enum(ARRANGEMENT_GROUP_ROLES),
  /** The order this one amends — `null` on a manual band, and when it is out of scope. */
  parent: z.object({ id: objectIdSchema, reference: z.string(), paNumber: z.string().nullable() }).nullable(),
});

/**
 * ⚠️ [C5c §2] ONE SHARED COLUMN, AS A READER GETS IT BACK — the input's shape, plus what the
 * column is CALLED, so a covered card can render "Inspector — named under DP1" without a
 * second lookup. The label is resolved from the master, or from the anchor's one-off line.
 */
export const sharedColumnSchema = z.object({
  lineKey: z.string(),
  anchorDutyPointId: z.string(),
  label: z.string(),
  shortLabel: z.string(),
  kind: z.enum(['post', 'vehicle', 'adhoc']),
  stashed: z.array(
    z.object({
      dutyPointId: z.string(),
      requiredCount: z.number().int().min(0),
      displayOrder: z.number().int().min(0),
      restrictToPostTypeIds: z.array(objectIdSchema),
      allocations: z.array(
        z.object({
          divisionOrgUnitId: objectIdSchema,
          unitOrgUnitId: objectIdSchema.nullable(),
          count: z.number().int().min(0),
        }),
      ),
    }),
  ),
});

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [P7B §5] WHERE A TYPED ORDER CAME FROM — stamped once, at creation, and never edited.
 *
 *  Keys and ordinals only. The ids of the template, its set and the run stay server-side; the list
 *  and the record render "From t12 · part 2 of 6" from this and read no template model.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const arrangementTemplateSourceSchema = z.object({
  setKey: z.string(),
  templateKey: z.string(),
  part: z.number().int().min(1),
  parts: z.number().int().min(1),
});

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [P7B §5] THE TYPED KEYS — OPTIONAL, AND OMITTED ON EVERY UNTYPED ORDER.
 *
 *  [P6d]'s test for optional versus nullable: does a caller who will never receive a value have to
 *  render something? No — an untyped order draws no chip, no template link and no pill, and the
 *  key's very presence is the signal. So an untyped response is byte-identical to the one before
 *  P7B, and `arrangementPreview.test.ts`'s pinned key set passes unedited.
 *
 *  `inChargeUnnamed` is sent on a typed DRAFT only: true while a by-name command role on it names
 *  nobody — the reason publish holds it back ([P7B] decision 2). A yes/no, never a count (FR-5.2.6b).
 * ─────────────────────────────────────────────────────────────────────────────────
 */
const typedArrangementKeys = {
  type: z.enum(ARRANGEMENT_TYPE_KEYS).optional(),
  templateSource: arrangementTemplateSourceSchema.optional(),
  inChargeUnnamed: z.boolean().optional(),
};

export const arrangementSchema = z.object({
  id: objectIdSchema,
  /** ⚠️ IMMUTABLE, MINTED ONCE. A row is never unidentifiable, even with no title. */
  reference: z.string(),
  paNumber: z.string().nullable(),
  /** ⚠️ What the operator typed, or `null`. Readers want `displayTitle`. */
  title: z.string().nullable(),
  /** Derived at render from the CURRENT places and date, so it cannot go stale. */
  displayTitle: z.string(),
  description: z.string().nullable(),
  /** ⚠️ [amended D4] Free text. Never parsed, never counted. */
  estimatedCrowdSize: z.string().nullable(),
  status: z.enum(ARRANGEMENT_STATUSES),
  /** ⚠️ Always `STANDARD` in P3a. The seam P3b's second tab filters on. */
  kind: z.enum(ARRANGEMENT_KINDS),
  /** ⚠️ [amended D4] ADMINISTRATIVE. The duty dates below are the operational answer. */
  paDate: calendarDateSchema,
  dutyPoints: z.array(dutyPointSchema),
  instructions: z.array(z.object({ type: z.literal('LINE'), text: z.string() })),
  supportingArrangements: z.array(z.object({ personName: z.string(), responsibilities: z.array(z.string()) })),
  /**
   * ⚠️ [C1b §4] RESOLVED, AND ON THE DETAIL PAYLOAD ONLY.
   *
   * `arrangementListItemSchema` deliberately gains nothing: §4b is visibility-only, and a
   * list column would invite exactly the reading — "these units owe me force" — that §4b
   * exists to refuse.
   */
  associatedOrgUnits: z.array(associatedUnitSchema),
  /**
   * ⚠️ [C5c §2] THE SHARED COLUMNS — REQUIRED, `[]` WHEN THERE ARE NONE, AND NEVER OPTIONAL.
   *
   * `group`'s argument, verbatim: an absent key is indistinguishable from an older server. Here
   * the cost is worse than a missing link — the builder rebuilds its form from this payload and
   * sends the array back on every save, so an absent key read as `[]` would END SHARING and
   * discard every stash on the next autosave, coherently enough that nothing refused it.
   */
  sharedColumns: z.array(sharedColumnSchema),
  /**
   * ⚠️ [C2b §4f] THE GROUP, ON THE DETAIL PAYLOAD FOR THE FIRST TIME.
   *
   * It was on `arrangementListItemSchema` alone, because a manual band is a LIST affordance and
   * a detail page had nothing to do with one. A corrigendum is different in kind: §4d keeps
   * every count per-arrangement — the parent reads its own 50 and the amendment its own 12 —
   * and §4f makes showing the link the whole compensation for that. A detail page that did not
   * say "this amends PA 5678" would be the screen where the split does the most damage.
   */
  group: arrangementGroupRefSchema.nullable(),
  /**
   * ⚠️ THE RECORDED SHAPE. Counts, not a label.
   *
   * The brief asks for the resulting shape to be recorded for the list and for reporting.
   * A stored LABEL ("multi-slot, concurrent") goes stale the moment a place is removed;
   * the numbers it would be computed from do not, because one writer recomputes them on
   * every write. So the numbers are stored and any label derives from them at render.
   *
   * ⚠️ [amended D4] THREE OF THESE ARE NOW SUMS AND ONE IS A UNION, AND THE DIFFERENCE IS
   * DELIBERATE. `placeCount` and `deploymentCount` SUM across duty points — two points at
   * two places really are two places and really will make two duties. `slotCount` and
   * `dutyDayCount` are the size of the UNION of slot keys and of duty dates, because "how
   * many distinct shifts does this order touch" is the question a reader is asking, and a
   * sum would make `slotCount === deploymentCount` and store one number twice.
   */
  dutyPointCount: z.number().int().min(0),
  placeCount: z.number().int().min(0),
  slotCount: z.number().int().min(0),
  dutyDayCount: z.number().int().min(0),
  deploymentCount: z.number().int().min(0),
  /**
   * ⚠️ [amended D4] THE OPERATIONAL DATE AXIS, DERIVED — the earliest and latest duty date
   * across every duty point, `null` while there are none.
   *
   * A list asking "which orders have duty between X and Y" ranges these two, never
   * `paDate`. They are two SCALARS rather than one array of every duty date, and that was
   * MEASURED rather than chosen: every scoped index leads with `visible_org_unit_ids`,
   * which is already multikey, and a compound index over two arrays BUILDS CLEANLY and
   * then fails every write with `cannot index parallel arrays`. A silent boot followed by
   * a broken save is strictly worse than a refused index.
   */
  firstDutyDate: calendarDateSchema.nullable(),
  lastDutyDate: calendarDateSchema.nullable(),
  /**
   * ⚠️ [P4] WHAT HAS ACTUALLY BEEN NAMED — derived on every read, stored nowhere.
   *
   * `allocatedCount` is what the divisions were asked to provide across every duty;
   * `namedCount` is how many people are against it. `fillState` reads COMPLETE only when
   * every addressed share on every duty is complete, so adding a division to a live order
   * makes it incomplete again with nothing to update (FR-5.3.6a). `FULLY_MAPPED` is
   * deliberately absent from `ARRANGEMENT_STATUSES` for the same reason.
   */
  allocatedCount: z.number().int().min(0),
  namedCount: z.number().int().min(0),
  fillState: z.enum(FILL_STATES),
  /**
   * ⚠️ [C5 §3] THE COMPLETION'S SECOND FACT — was it DECLARED short?
   *
   * `COMPLETE` + `closedShort` renders `Closed short`; `COMPLETE` alone renders `All Filled`.
   * A client reported a live order reading "Complete" with nobody named: every division had
   * closed its share at `0 of 12` with a reason, which IS complete by decision and is the one
   * thing this product must not call filled.
   *
   * ⚠️ A SECOND AXIS, NEVER A FIFTH `FILL_STATES` MEMBER. A member would move
   * `allocation_fills.fill_rank`'s stored `{min: 0, max: 3}` and re-open every query reading
   * it — [LIFECYCLE]'s rule, after `archived_at`, `DUTY_EVENT_ATTESTATIONS` and `PostAudience`.
   *
   * ⚠️ REQUIRED KEY, BOOLEAN VALUE — never optional. Every surface that renders the state
   * renders a word either way, so §6's test says `false` answers the question the screen is
   * asking; `undefined` would make an older server indistinguishable from a full close and
   * fail the label OPEN, which is the direction that reads "filled".
   *
   * ⚠️ [amended C5b §3] ITS MEANING NARROWED, AND IT WAS WRONG BEFORE IT NARROWED. It is now
   * "COMPLETE, a share was DECLARED short, AND the names are short" — `fillOfTotals`. C5's MQL
   * asked `$ne: null` of a field most shares LACK, so it read `true` on every fully auto-locked
   * order (F1); and an order closed short that then found its people reads COMPLETE without it
   * (S09). So a COMPLETE that is not closed short has `named >= allocated > 0`, and a client may
   * rely on that: `All Filled` always has its names. Word it through `fillWordOf`.
   */
  closedShort: z.boolean(),
  /**
   * ⚠️ [C1e §2b] THE PREVIEW'S FOUR NUMBERS ARE `required · sourced · named · reached`, AND
   * ONLY TWO OF THEM ARE NEW. [amended C5c §5a] THREE FIGURES AND ONE LINE NOW — `reached` went,
   * replaced by `presence` below. `sourced` IS `allocatedCount` above and `named` IS
   * `namedCount` — one expression each, so the modal and the detail page cannot disagree.
   *
   * ⚠️ `slotCount` IS NOT `required`. It is the size of the UNION of slot keys — how many
   * distinct shifts this order touches — and reading it as force is the mistake this comment
   * exists to prevent. `required` is the sum of the DUTIES' stamped requirement, which is
   * what `REQUIREMENT_FULFILMENT` counts and is computed by the same one pipeline.
   *
   * ⚠️ BOTH ARE `.nullable().optional()`, AND THE TWO ANSWER DIFFERENT QUESTIONS.
   * **Optional** = the preview did not run, so `?view=record` is byte-for-byte what it always
   * was. **Null** = it ran and there is nothing to derive this from. Collapsing them makes
   * "the modal was opened on a draft" indistinguishable from "the detail page asked", which
   * are opposite screens.
   *
   * ⚠️ AND A ZERO WOULD BE A CLAIM. `0` says "nobody turned up" — a red statement on a policing
   * screen — about an order whose window has not opened. That is `attendance.service.ts`'s own
   * `NOT_STARTED`-versus-`ABSENT` split: absence is a property of the clock.
   */
  required: z.number().int().min(0).nullable().optional(),
  /**
   * ⚠️ [C5c §5a] THE ORDER'S `Present · Absent · Not arrived`, AND IT REPLACED `reached`.
   *
   * `reached` counted `REACHED` rows alone, so an officer who stood down after reaching — present by
   * any reading — was not counted, and `null` meant "no duty event anywhere". The client asked for
   * three states on this screen, `presenceOf` is the one reduction, and this is its tally over every
   * NAMED officer on every duty that has STARTED (`presenceOfRows`). An unmarked, ended duty reads
   * Absent here exactly as it does on the printed order — `markingsRecorded` is retired with it.
   *
   * ⚠️ NAMED `presence`, NEVER `attendance`: the fill slot's `attendance` is the six-member enum.
   * ⚠️ `.nullable().optional()`, [C1e §2]'s two questions unchanged: optional = the preview did not
   * run, null = no duty of this order has started, so there is nobody to count yet.
   */
  presence: presenceTallySchema.nullable().optional(),
  /** Divisions this arrangement will ask to fill it. Drives the list column. */
  divisions: z.array(orgUnitSummarySchema),
  publishedAt: z.iso.datetime().nullable(),
  publishedByOfficerId: objectIdSchema.nullable(),
  createdByOfficerId: objectIdSchema,
  /** ⚠️ The POSTING unit of the officer who raised it — never the acting one; `raisedAtLevel` below
      is the level an elevated raiser acted at ([C5c §11f]). */
  createdByOrgUnitId: objectIdSchema,
  /**
   * ⚠️ [P7A §2] THE LEVEL THIS ORDER WAS RAISED AT — `arrangementListItemSchema.raisedAtLevel`'s
   * fact, the same derivation, on the record (and so on `?view=preview`, a superset of it). One
   * `loadListRefs` read already resolves the raiser's unit, so it costs the record no new query.
   * `null` = the raiser's unit does not resolve: render no pill.
   */
  raisedAtLevel: z.enum(RAISER_LEVELS).nullable(),
  archivedAt: z.iso.datetime().nullable(),
  /**
   * [LIFECYCLE] The status held when it was archived. Restore puts it back, and the detail
   * screen reads it to say "Archived · was published" rather than replacing the status.
   */
  archivedFromStatus: z.enum(ARRANGEMENT_STATUSES).nullable(),
  purgeAt: z.iso.datetime().nullable(),
  /**
   * ⚠️ [C5c §8] WHEN THIS DRAFT'S THIRTY DAYS STARTED — `DRAFT_MAX_AGE_DAYS`' clock, and the date the
   * builder's expiry notice counts from.
   *
   * `null` on anything that is not a DRAFT: publish clears the column, so an issued order carries no
   * second answer to "will this be destroyed?" — `purgeAt`'s own rule. Also `null` on a legacy draft
   * the backfill has not reached, which the sweep never touches (it fails closed).
   */
  draftSince: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  ...typedArrangementKeys,
  /**
   * ⚠️ [P7B §6b] SET WHEN A MASTER SUPER ADMIN TOOK THIS ORDER BACK TO DRAFT WHILE IT WAS RUNNING —
   * the banner's facts. Optional: absent on every order that was never taken back, and publish clears
   * it. The super admin is deliberately not named here; the audit row carries who.
   */
  revertedToDraft: z.object({ at: z.iso.datetime(), reason: z.string() }).optional(),
});

/** The list row. Deliberately narrower than the full record — a page of 20 of those is large. */
/**
 * What a LIST ROW knows about the group it is listed under. [P3b.1]
 *
 * ⚠️ IT LIVES HERE, NOT IN `arrangementGroup.schema.ts`, TO KEEP THE DEPENDENCY ONE-WAY.
 * The group schema needs `arrangementSummarySchema` and `calendarDateSchema` from this
 * file; if this file also imported from that one, the two would form a cycle — which zod
 * survives only as long as the evaluation order cooperates, and esbuild is free to reorder.
 * `arrangementGroup.schema.ts` re-exports this, so a reader still finds it where they look.
 *
 * ⚠️ `memberCount` IS THE NUMBER OF MEMBERS *THIS CALLER* CAN SEE, NOT THE GROUP'S SIZE.
 * A group may span two divisions, and FR-3.6 makes their arrangements mutually invisible;
 * the array's own length would publish the other half's existence through a number.
 */
export const arrangementListItemSchema = z.object({
  id: objectIdSchema,
  reference: z.string(),
  paNumber: z.string().nullable(),
  displayTitle: z.string(),
  status: z.enum(ARRANGEMENT_STATUSES),
  kind: z.enum(ARRANGEMENT_KINDS),
  /** ⚠️ [amended D4] ADMINISTRATIVE, and still the default sort — see `firstDutyDate`. */
  paDate: calendarDateSchema,
  /**
   * ⚠️ [amended D4] REPLACED `locationMode`. A count rather than a label, for the reason
   * the record's own counts are counts: "3 duty points · 5 places" stays true when a point
   * is removed, and "CONCURRENT" does not.
   */
  dutyPointCount: z.number().int().min(0),
  /** The operational span, for the list's date filter and the `firstDutyDate` sort. */
  firstDutyDate: calendarDateSchema.nullable(),
  lastDutyDate: calendarDateSchema.nullable(),
  /**
   * [P3b.1] The group this order is listed under, if any. Presentational ONLY — see
   * `arrangementGroup.schema.ts`. ⚠️ `memberCount` is what THIS caller can see.
   */
  group: arrangementGroupRefSchema.nullable(),
  /** First place of the FIRST duty point, plus a count, so the column is one line. */
  primaryLocationName: z.string().nullable(),
  placeCount: z.number().int().min(0),
  slotCount: z.number().int().min(0),
  dutyDayCount: z.number().int().min(0),
  deploymentCount: z.number().int().min(0),
  /**
   * ⚠️ [amended D4] THE FIRST DUTY POINT'S POLICY, and `null` when there is no duty point.
   * An order may now span several policies; the list column says so with a `+N` rather than
   * pretending there is one. The `policyId` FILTER matches an order any of whose duty
   * points names it.
   */
  policyId: objectIdSchema.nullable(),
  policyName: z.string().nullable(),
  policyCount: z.number().int().min(0),
  divisions: z.array(orgUnitSummarySchema),
  /**
   * ⚠️ [P4] TWO DIFFERENT TRUE STATEMENTS, AND THEY DO NOT COLLAPSE INTO ONE.
   *
   * `allocatedCount` is how much the divisions were ASKED TO PROVIDE and `namedCount` is
   * how many people have actually been put against it. P3a.1 recorded the first as "a true
   * statement about the order rather than a claim about people"; this pass adds the claim
   * about people beside it rather than redefining it. An order allocating 10 with 6 named is
   * a real order in mid-flight, and rendering one number where there are two is how "8 of
   * 12" quietly becomes "12".
   *
   * ⚠️ FORCE ONLY. A command officer can never reach either — FR-5.2.6b, and
   * `commandRoleIsolation.test.ts` CLAIM 5 scans for the arithmetic that would let one.
   */
  allocatedCount: z.number().int().min(0),
  namedCount: z.number().int().min(0),
  /**
   * ⚠️ DERIVED ON EVERY READ, NEVER STORED (FR-5.3.6a).
   *
   * An order reads COMPLETE when every addressed share on every duty is complete. Divisions
   * get added to live orders, and a stored flag would leave a green order carrying unfilled
   * slots — the worst available failure for a policing dashboard, because the wrong number
   * is the confident one. `FULLY_MAPPED` is deliberately absent from `ARRANGEMENT_STATUSES`
   * for the same reason; this is a computed VIEW, not a state the machine can enter.
   */
  fillState: z.enum(FILL_STATES),
  /** ⚠️ [C5 §3] See `arrangementSchema.closedShort` — the same second axis, on the list row, and
      the same [C5b §3] narrowing: `All Filled` without it always has its names. */
  closedShort: z.boolean(),
  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [amended P7A §2] AT WHICH LEVEL WAS THIS ORDER RAISED? — `HQ` · `DIVISION` · `UNIT`, or
   *  `null`. The web draws it as a pill reading HQ / DRO / Unit; Duty Mitra the same words.
   *
   *  ⚠️ THREE VALUES, REPLACING [C5c §10]'s BOOLEAN `raisedAtCommissionerate`. The client asked for
   *  every order to say its level, and a boolean can name one of them; keeping both would be two
   *  vocabularies for one fact, so the boolean is DELETED rather than kept beside it.
   *
   *  NAMED FOR THE FACT, NOT THE WORD. "DRO" is the client's display word for `DIVISION`; a
   *  Bengali screen or a CSV export says something else about the same fact. ⚠️ And `DIVISION` is a
   *  LEVEL, not the DRO reserve-office unit — `RAISER_LEVELS`' header says why the word collides.
   *
   *  ⚠️ §10b — THE RAISER, NEVER THE ADDRESSEE. It is derived from the RAISER's unit —
   *  `raised_at_org_unit_id` (the level an ELEVATED raiser acted at, stamped at raise time and never
   *  rewritten, [C5c §11f]) else `created_by_org_unit_id` — and `division_org_unit_ids` is not read:
   *  who raised an order and who it is addressed to are different facts, and `divisions` beside it
   *  already carries the second.
   *
   *  ⚠️ DERIVED ON EVERY READ FROM THAT UNIT'S **POSITION** (`raiserLevelOf`), with the `type` label
   *  only as a belt — the raiser UNIT may be stamped, the answer never is. A divisional nodal can
   *  relabel their own node ([C1e §5] measured it), and a label-keyed level would then put "HQ" on
   *  their orders in every other division's list; under this rule a relabel can only UNDER-claim (a
   *  division labelled COMMISSIONERATE reads `UNIT`). The accepted consequences, stated: a
   *  SUPER_ADMIN posted at a division raises `DIVISION` orders (only the posting unit, or the acting
   *  unit when elevated, is stored — never the role); a later reparent changes the answer for orders
   *  already issued; a generated order inherits its standing order's raiser.
   *
   *  ⚠️ REQUIRED KEY, NULLABLE VALUE — `closedShort`'s rule one key up: every row renders either way,
   *  and `undefined` would make an older server indistinguishable from "no pill". `null` means the
   *  raiser's unit id resolves to no org unit, and every client draws NOTHING for it.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  raisedAtLevel: z.enum(RAISER_LEVELS).nullable(),
  archivedAt: z.iso.datetime().nullable(),
  /** [LIFECYCLE] So an archived row can say what it WAS, not merely that it is archived. */
  archivedFromStatus: z.enum(ARRANGEMENT_STATUSES).nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  ...typedArrangementKeys,
});

export const arrangementSummarySchema = z.object({
  id: objectIdSchema,
  reference: z.string(),
  displayTitle: z.string(),
  paDate: calendarDateSchema,
});

// ── Actions ──────────────────────────────────────────────────────────────────────

/**
 * ⚠️ THE THREE BUTTONS, AS ONE FIELD.
 *
 * `Save draft` is a `PATCH` and never reaches here. The other two both publish and differ
 * only in what happens next: `REQUEST_DIVISIONS` hands the duties to the divisions to fill
 * (P4 builds the inbox), `ASSIGN_MYSELF` keeps them with the raising office. P3a generates
 * identical deployments for both and RECORDS the intent — the stub is the screen that
 * follows, not the field.
 */
export const PUBLISH_INTENTS = ['REQUEST_DIVISIONS', 'ASSIGN_MYSELF'] as const;
export type PublishIntent = (typeof PUBLISH_INTENTS)[number];

export const publishArrangementSchema = z.object({
  intent: z.enum(PUBLISH_INTENTS).default('REQUEST_DIVISIONS'),
});

/** What publishing will create, answered before it happens. */
export const publishPreviewSchema = z.object({
  deploymentCount: z.number().int().min(0),
  slotCount: z.number().int().min(0),
  dutyDayCount: z.number().int().min(0),
  placeCount: z.number().int().min(0),
  /** ⚠️ [amended D4] The ceiling is a SUM over these, never a product — see §3. */
  dutyPointCount: z.number().int().min(0),
  /**
   * ⚠️ [amended D4] WHICH POLICY VERSIONS PUBLISHING WILL FORK, STATED BEFORE THE CLICK.
   *
   * §5c: changing a duty point's column SET forks a new PUBLISHED version and re-points
   * that duty point. It is an irreversible write on ANOTHER aggregate, so it is disclosed
   * in the preview by the same function the write path calls — `tryPlanFill`'s manners,
   * applied to a different kind of surprise. Empty on the common path.
   */
  policyForks: z.array(
    z.object({
      dutyPointId: z.string(),
      dutyPointTitle: z.string(),
      policyId: objectIdSchema,
      policyName: z.string(),
      fromVersionNumber: z.number().int().min(1),
      toVersionNumber: z.number().int().min(1),
      addedColumnCount: z.number().int().min(0),
    }),
  ),
  /** ⚠️ FORCE ONLY. A command officer can never reach this number — see the DTO note. */
  personnelCount: z.number().int().min(0),
  divisions: z.array(orgUnitSummarySchema),
  hasOvernight: z.boolean(),
  /** ⚠️ WHAT MAKES PUBLISH IMPOSSIBLE. Structural only — see `warnings` for the rest. */
  blockers: z.array(z.string()),
  /**
   * ⚠️ WHAT THE OPERATOR SHOULD KNOW, AND PUBLISH ANYWAY.
   *
   * The distinction is deliberate and it is about the ROLL-OUT, not about taste: on day one
   * the establishment is not fully mapped, so an order that names nobody for a required
   * command role is the NORMAL case, not a malformed one. Blocking publish on it would mean
   * no arrangement could be raised until the officer master was complete, which is the
   * wrong order of work. A required role with nobody named is therefore a warning; a
   * missing place, no dates, nobody asked for and an unbalanced column stay blockers,
   * because each of those makes the deployments themselves undefined.
   */
  warnings: z.array(z.string()),
});

export const restampResultSchema = z.object({
  updated: z.number().int().min(0),
  exceptionsSkipped: z.number().int().min(0),
});

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C1d §1] ARCHIVING AN ELAPSED ORDER IS **FILING**, AND IT IS THE COMPLEMENT OF
 *  [LIFECYCLE] §3a RATHER THAN A REVERSAL OF IT.
 *
 *  §3a made archiving a published order a CANCELLATION: it moves the order to `CANCELLED`,
 *  emits `ARRANGEMENT_CANCELLED` naming every assigned officer and every addressed unit, and
 *  refuses outright once a duty window has opened. That is exactly right for withdrawing
 *  something that has NOT HAPPENED. It left no way at all to put away an order that is OVER —
 *  `hasOpenedWindow` is `starts_at <= now`, so every elapsed order trips it.
 *
 *      window not yet open   →  CANCELLATION.  Unchanged, in every particular.
 *      window open, running  →  BLOCKED.       Unchanged. A duty in progress is a record.
 *      every duty ended      →  FILING.        NEW. Archived, and nobody is told anything.
 *
 *  ⚠️ THE BLOCKED MIDDLE STAYS BLOCKED. Filing does not widen what may be withdrawn; it adds
 *  a third act to the far side of the same guard.
 *
 *  ⚠️ AND `hasDutyEvent` IS NO LONGER THE REDUNDANT SEAM ITS OWN COMMENT CLAIMS.
 *  `deployment.schema.ts` still records "`hasDutyEvent ⟹ hasOpenedWindow` in this release",
 *  which was true when nothing wrote `ACTIVE`/`CLOSED`. `closeFinishedDuties` writes `CLOSED`
 *  on every duty whose `ends_at` has passed and `dutyEvent.service.ts` writes `ACTIVE` on the
 *  first marking, so an elapsed order now trips BOTH refusals and the tier has to answer both.
 *
 *  ⚠️ THE TUPLE READS AS ASCENDING CONSEQUENCE — tells nobody, tells officers, refused —
 *  mirroring `UNPUBLISH_TIERS`. It is deliberately NOT the branch order, which is written
 *  above the expression in `deleteImpact` because the ORDER IS THE POLICY and inverts
 *  un-publish's: there `FREE` is the residue, because falling through to it only under-warns.
 *  Here **BLOCKED is the residue**, because falling through to CANCELLATION would text forty
 *  officers about a duty they already stood, and a message cannot be recalled.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C1e §2] WHICH VIEW OF ONE ARRANGEMENT IS BEING ASKED FOR.
 *
 *  ⚠️ THE MEMBERS ARE `record | preview` AND **NOT** `full | skeleton`. Those are
 *  `APP_DETAIL_VIEWS`' members with the OPPOSITE default (`full`), and two tuples spelled
 *  identically with different defaults is worse than two different words. `record` says what
 *  the payload IS — the order as recorded — so the day a third view arrives nobody has to
 *  work out which one "default" meant.
 *
 *  ⚠️ `record` IS THE DEFAULT, because a new mode breaks nobody and a new default breaks
 *  everybody silently — [P5b]'s rule, verbatim.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const ARRANGEMENT_VIEWS = ['record', 'preview'] as const;
export type ArrangementView = (typeof ARRANGEMENT_VIEWS)[number];

export const getArrangementQuerySchema = z.object({
  view: z.enum(ARRANGEMENT_VIEWS).default('record'),
});

export const ARCHIVE_TIERS = ['FILING', 'CANCELLATION', 'BLOCKED'] as const;
export type ArchiveTier = (typeof ARCHIVE_TIERS)[number];

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C2b §4g] WHAT ELSE GOES WITH IT. ONE ROW PER OTHER MEMBER OF THE CORRIGENDUM FAMILY.
 *
 *  §4g maps the lifecycle to the family: cancelling the parent cancels the corrigendum, and
 *  un-publishing the parent takes the corrigendum with it and discards its assignments too.
 *  A confirmation that does not SAY so lets an operator withdraw two orders believing they
 *  withdrew one — which is exactly the class of surprise `deleteImpact` exists to prevent.
 *
 *  ⚠️ A LIST OF THE OTHERS, NEVER A SUM FOLDED INTO THE EXISTING TOTALS. Every number beside
 *  it keeps meaning what it has always meant — this order's own — so no renderer, no test and
 *  no audit context changes meaning under anyone. §4g's "the confirmation states BOTH counts"
 *  is only expressible if the two are still two.
 *
 *  ⚠️ REQUIRED AND EMPTY IN THE ORDINARY CASE, NEVER `.optional()`. An absent key is
 *  indistinguishable from "there is no corrigendum", and that ambiguity fails OPEN: the screen
 *  renders no warning and the cascade happens anyway. [C1f §1]'s `delegatedFromAllocKey` rule.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const impactCascadeMemberSchema = z.object({
  id: objectIdSchema,
  reference: z.string(),
  paNumber: z.string().nullable(),
  role: z.enum(ARRANGEMENT_GROUP_ROLES),
  deploymentCount: z.number().int().min(0),
  /** Named rows only — the same population `unpublishImpact.assignmentCount` counts. */
  assignmentCount: z.number().int().min(0),
  /**
   * ⚠️ [C6a] THE MEMBER'S OWN `unpublishImpact.commandSeatsNamed`, AND FOR THE SAME REASON. A
   * corrigendum can ask a division for command and be answered, and un-publishing its parent
   * deletes those seats and their names — so "0 names" on its row must not be the whole sentence.
   * A flag, never a number: command enters no figure (FR-5.2.6b).
   */
  commandSeatsNamed: z.boolean(),
});
export type ImpactCascadeMemberDto = z.infer<typeof impactCascadeMemberSchema>;

export const deleteImpactSchema = z.object({
  deploymentCount: z.number().int().min(0),
  /**
   * How many officers this affects — stated before the click, never after.
   *
   * ⚠️ [LIFECYCLE] IT IS REAL NOW. It was hardcoded `0` with the comment "P4 names
   * officers; until then nobody is assigned"; P4 has since landed, so the comment had
   * become false and the number a lie. It counts DISTINCT officers holding an ASSIGNED
   * slot — distinct, because an officer named into three shifts of one order is one
   * person who gets one message.
   */
  affectedOfficerCount: z.number().int().min(0),
  /** How many divisions and units will be told — the addressees of the fill request. */
  affectedOrgUnitCount: z.number().int().min(0),
  /**
   * ⚠️ ARCHIVING THIS IS A CANCELLATION, NOT FILING. Drives the WHOLE confirmation: a
   * draft says "Delete this draft?" and states that nobody has been told; an issued order
   * says "Cancel this order?" and states who will be. One title for both hides the
   * difference §3a exists to make.
   */
  isIssued: z.boolean(),
  /**
   * `true` when a duty window has already opened.
   *
   * ⚠️ [amended C1d §1] IT NO LONGER REFUSES THE ARCHIVE ON ITS OWN. It used to, and that is
   * precisely what made an order that had finished unfilable. It is now one of the two halves
   * of "has this reached the ground"; whether the act is refused is `tier`.
   */
  hasOpenedWindow: z.boolean(),
  /**
   * `true` when a duty under this order has reached the ground. Its own refusal, because
   * it needs different advice — see `DEPLOYMENT_STATUSES_ON_THE_GROUND`.
   *
   * ⚠️ [amended C1d §1] "IT CAN ONLY BE TRUE WHEN `hasOpenedWindow` ALREADY IS" WAS TRUE WHEN
   * NOTHING WROTE THOSE STATUSES, AND IS NOT ANY MORE. `dutyEvent.service.ts` writes `ACTIVE`
   * on the first marking, which a supervisor's Mark Reached can produce before the window
   * opens (FR-5.4.5) — the case `deployment.schema.ts` predicted would separate them.
   */
  hasDutyEvent: z.boolean(),
  /**
   * ⚠️ [C1d §1] `true` when ANY duty of this order is still to end — `ends_at > now`.
   *
   * The EXACT COMPLEMENT of `closeFinishedDuties`' own `ends_at <= now`, so "every duty the
   * lifecycle tick would have closed" and "filable" are the same set by construction rather
   * than by coincidence. ⚠️ `false` on a DRAFT, which has no duties at all — which is why the
   * branch order sends a draft to CANCELLATION before it is ever asked.
   */
  hasUnfinishedDuty: z.boolean(),
  /**
   * ⚠️ [C1d §1] WHICH OF THE THREE ACTS THIS IS. The confirmation branches on this and on
   * nothing else — a client re-deriving it from the three booleans would be a second
   * expression of the policy, and the two would disagree the day a fourth reason to refuse
   * is added.
   */
  tier: z.enum(ARCHIVE_TIERS),
  /** ⚠️ [C2b §4g] The other members of this order's corrigendum family, which archive with it. */
  cascade: z.array(impactCascadeMemberSchema),
});

export const duplicateArrangementSchema = z.object({
  paDate: calendarDateSchema.optional(),
});

// ── [D4.4 §5] Un-publish ─────────────────────────────────────────────────────────

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [D4.4 §5] `PUBLISHED → DRAFT`. THIS REVERSES A DECISION [D4.3] RECORDED, AND THAT IS
 *  DELIBERATE RATHER THAN AN OVERSIGHT.
 *
 *  D4.3's changelog says, of its own §5: "**Nothing in this system can un-publish an
 *  arrangement**, and [LIFECYCLE] treats withdrawing an issued order as a CANCELLATION that
 *  emits events. Confirmed with the user before building: **D4.3 adds no status
 *  transition.**" D4.4 adds it, on a later instruction. Whoever reads that paragraph next
 *  should find this one.
 *
 *  ⚠️ IT IS NOT ARCHIVE, AND THE TWO MUST NOT BE ROUTED THROUGH EACH OTHER.
 *  Archive withdraws an order from circulation and REMEMBERS what it was — `archived_at`,
 *  `archived_from_status`, `purge_at`, and `CANCELLED` on an issued one. Un-publish returns
 *  a LIVE order to editing: no archive column is written, the status goes to `DRAFT`, and
 *  the order stays in the current list because it is still current. An operator who
 *  un-publishes has not withdrawn anything from anybody's records; they have taken the
 *  order back to change it.
 *
 *  ⚠️ THE THREE TIERS ARE ABOUT WHAT HAS ALREADY BEEN TOLD TO WHOM.
 *    · nothing named, no window open  → free. Nobody has heard about a specific person.
 *    · officers named, no window open → allowed, but the confirmation states the counts and
 *      a withdrawal event is emitted, because those names were sent to divisions.
 *    · any window opened, or any duty event → REFUSED. That is a record of what happened on
 *      the ground, and a record is not editable.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const UNPUBLISH_TIERS = ['FREE', 'DISCARDS_ASSIGNMENTS', 'BLOCKED'] as const;
export type UnpublishTier = (typeof UNPUBLISH_TIERS)[number];

/**
 * What un-publishing will destroy, answered BEFORE the click — `tryPlanFill`'s rule, and
 * `deleteImpact`'s. Computed by the same function the write path calls, so the sentence an
 * operator reads and the refusal they may hit cannot disagree.
 */
export const unpublishImpactSchema = z.object({
  /** Duties that will be discarded. Re-publishing regenerates them. */
  deploymentCount: z.number().int().min(0),
  /**
   * ⚠️ ASSIGNMENTS AND OFFICERS ARE TWO DIFFERENT NUMBERS, AND THE CONFIRMATION SAYS BOTH.
   * One officer named into six shifts is six assignments and one person: "6 names" tells an
   * operator what work is lost, "5 officers" tells them how many people were affected.
   */
  assignmentCount: z.number().int().min(0),
  affectedOfficerCount: z.number().int().min(0),
  /** The addressed divisions and units — who receives the withdrawal event. */
  affectedOrgUnitCount: z.number().int().min(0),
  hasOpenedWindow: z.boolean(),
  hasDutyEvent: z.boolean(),
  tier: z.enum(UNPUBLISH_TIERS),
  /**
   * ⚠️ [C6a] WHETHER A DIVISION HAS NAMED ANYBODY INTO A COMMAND POSITION IT WAS ASKED FOR. A flag,
   * never a number — command enters no count (FR-5.2.6b) — and it is why `tier` reads
   * `DISCARDS_ASSIGNMENTS` for an order whose only names are commanders, with `assignmentCount` 0.
   * The confirmation reads it so "0 names … will be discarded" is not the whole sentence.
   */
  commandSeatsNamed: z.boolean(),
  /**
   * ⚠️ [C6a review] WHETHER ANY DESK WAS ASKED FOR A COMMAND POSITION ON THIS ORDER, named or not. Every
   * such desk is TOLD when the order is taken back, and none is in `affectedOrgUnitCount`, which stays
   * the force's figure — so the confirmation says they are told too, in words. A flag, never a number.
   */
  commandSeatsAsked: z.boolean(),
  /**
   * ⚠️ [C2b §4g] The other members of this order's corrigendum family, taken back with it —
   * their assignments discarded too. `assignmentCount` on each is that member's own.
   */
  cascade: z.array(impactCascadeMemberSchema),
});

export const unpublishArrangementSchema = z.object({
  /**
   * ⚠️ REQUIRED, AND IT REACHES THE AUDIT ROW. Un-publishing destroys named work that other
   * offices did; "who did this and why" is the question somebody asks a week later, and a
   * free-text box nobody must fill in is a box that is always empty.
   */
  reason: z.string().trim().min(1, 'Say why this is being taken back').max(500),
});

// ── [P7B §6] The two master super admin overrides ────────────────────────────────

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [P7B §6a] DISCARD MANY DRAFTS AT ONCE — each one exactly the Discard a single draft gets
 *  (archived, restorable for thirty days), in its own transaction, so one refusal never undoes another.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const BULK_DISCARD_MAX = 100;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [LIST-FILTERS] HOW MANY DIVISIONS ONE LIST REQUEST MAY NAME — HEADROOM, NOT A COUNT.
 *
 *  The commissionerate has 36 divisions today (ten territorial, 26 wings). A bound AT 36 would
 *  turn "every division" into a 422 the day a 37th is added, on both order lists in every tab
 *  until somebody cleared the filter — so this is 50. It bounds the request and the `$in` the
 *  service builds from it; the web caps its picker and its stored filter at this same constant,
 *  so the browser can never send a list the schema refuses.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const ARRANGEMENT_LIST_DIVISIONS_MAX = 50;

export const bulkDiscardSchema = z.object({
  arrangementIds: z
    .array(objectIdSchema)
    .min(1)
    .max(BULK_DISCARD_MAX)
    .refine((ids) => new Set(ids).size === ids.length, { message: 'An order is listed twice' }),
});

export const BULK_DISCARD_OUTCOMES = ['DISCARDED', 'REFUSED'] as const;
export type BulkDiscardOutcome = (typeof BULK_DISCARD_OUTCOMES)[number];

export const bulkDiscardResultSchema = z.object({
  items: z.array(
    z.object({
      arrangementId: objectIdSchema,
      outcome: z.enum(BULK_DISCARD_OUTCOMES),
      /** The sentence for a refusal — not a draft, already archived, part of a corrigendum family. */
      reason: z.string().nullable(),
    }),
  ),
  discarded: z.number().int().min(0),
  refused: z.number().int().min(0),
});

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [P7B §6b/§6c] TAKING A RUNNING ORDER BACK TO DRAFT — A DELIBERATE PIERCING OF [C1d].
 *
 *  Every figure the confirmation states is EXACT and is counted again inside the transaction: the
 *  revert is refused with a 409 if any of them moved after the dialog was read, because a person who
 *  confirmed "12 duty events will be discarded" must not discard 13.
 *
 *  ⚠️ FORCE FIGURES ONLY, AND A YES/NO FOR COMMAND. Whether an in-charge was named is a flag, never a
 *  number (FR-5.2.6b) — and it is not named `commandSeats…`, which the web's command census refuses.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const revertCountsSchema = z.object({
  /** Duties that will be deleted. */
  deploymentCount: z.number().int().min(0),
  /** Empty slots that will be deleted. */
  emptySlotCount: z.number().int().min(0),
  /** Names on duties — one officer on six shifts is six — and the distinct people behind them. */
  namedSlotCount: z.number().int().min(0),
  namedOfficerCount: z.number().int().min(0),
  /** Markings already recorded on the ground: started, reached, stood down, absent. */
  dutyEventCount: z.number().int().min(0),
  /** "You are on this duty" notifications already sent to officers. */
  dutyNotificationCount: z.number().int().min(0),
});

export const revertImpactSchema = revertCountsSchema.extend({
  /** True when the order may be taken back: running, not finished, not archived, not in a family. */
  eligible: z.boolean(),
  /** Why not, in a sentence, when `eligible` is false. */
  refusal: z.string().nullable(),
  /** Whether any in-charge was named on a duty or a command position. A flag, never a count. */
  inChargeNamed: z.boolean(),
});

export const revertArrangementSchema = z.object({
  reason: z.string().trim().min(1, 'Say why this is being taken back').max(500),
  /** The counts the confirmation showed. A mismatch is a 409 — never a silent difference. */
  expected: revertCountsSchema,
});

// ── List query ───────────────────────────────────────────────────────────────────

/**
 * ⚠️ [C3c §8a] THE LIST'S DEFAULT WINDOW — today and the next seven days.
 *
 * ⚠️ A SECOND CONSTANT RATHER THAN `INBOX_DEFAULT_WINDOW_DAYS`, WHICH IS ALSO 7. They are two
 * different facts — FR-5.3.1's inbox window, and this list's — that happen to share a number
 * today, and sharing the literal would make tuning one silently move the other. [WINGS.1]'s rule
 * is about the opposite case: two literals for ONE fact.
 */
export const ARRANGEMENT_DEFAULT_WINDOW_DAYS = 7;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C3c.1] THE WINDOW A CALLER SENDS WHEN IT MEANS "EVERY ORDER, WHENEVER IT RAN".
 *
 *  §8a's default is FORWARD — today and the next seven days — because that is what an operator
 *  opening Manage Arrangements is looking for. It is the WRONG filter for the two in-app links
 *  whose whole subject is the PAST, and both sent no dates and were silently emptied by it:
 *
 *    • `Start from a previous one` — a copy picker over the ten most recent orders by ORDER
 *      date, every one of which has usually finished;
 *    • a standing order's `Generated` count — a link meaning "show me everything this generator
 *      produced", which is a set that begins in the past.
 *
 *  ⚠️ AN EXPLICIT WINDOW, NOT A "NO WINDOW" ESCAPE. Adding a way to ask for all dates would put
 *  the pre-§8a behaviour back within reach of any caller, including a hand-typed URL, and the
 *  default exists precisely so that nobody has to think about it. This is a caller stating what
 *  it means.
 *
 *  ⚠️ AND THE COST IS STATED: the EXPLICIT arm does not admit an order with no duty dates
 *  (§8a's own rule — an operator who names a window has asked about duty), so an unscheduled
 *  DRAFT does not appear in the copy picker. That is right for it: a draft with no schedule has
 *  nothing to copy beyond its header.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const ARRANGEMENT_ANY_DATE_WINDOW = { dateFrom: '2000-01-01', dateTo: '2100-01-01' } as const;

/**
 * ⚠️ [amended C3c §8a] `firstDutyDate` JOINS THE ALLOWLIST, AND IT NEEDS NO NEW INDEX.
 *
 * §7 requires an index behind every `sortBy` value, and
 * `arrangementSchema.index({visible_org_unit_ids: 1, first_duty_date: 1})` already exists — built
 * by [D4] for the operational overlap filter, ascending on exactly this column. So this is NOT a
 * `pnpm seed:indexes` deploy step.
 *
 * ⚠️ THE HONEST CAVEAT: at a GLOBAL scope the leading term is absent and no index serves the
 * sort. That is ALREADY true of `paDate` — there is no bare `{pa_date: -1}` index either — so the
 * new field is exactly as well and as badly served as the one it replaces as the default, and the
 * gap is pre-existing and covers both.
 */
/*
 * ⚠️ [amended C5c §9] `updatedDay` — THE IST DAY OF THE LAST UPDATE, THEN THE DUTY DATE WITHIN IT.
 *
 * `updatedAt` is an instant, so a secondary sort under it almost never fires; the client asked for
 * the DAY of update to group and the duty date to order inside the day. It is the first `sortBy`
 * value no index serves: the day is derived in an aggregation (`listArrangements`), bounded by the
 * same scope and window every other sort is. The API default stays `firstDutyDate`; the Manage
 * Arrangements page defaults to this one.
 */
export const ARRANGEMENT_SORT_FIELDS = [
  'firstDutyDate',
  'paDate',
  'createdAt',
  'updatedAt',
  'reference',
  'updatedDay',
] as const;
export type ArrangementSortField = (typeof ARRANGEMENT_SORT_FIELDS)[number];

/*
 * ⚠️ [amended C3c.1] `'asc'` — AND THE DIRECTION HAD TO MOVE WITH THE FIELD.
 *
 * [C3c §8a] made `firstDutyDate` the default sort and meant "soonest first"; `basePaginationSchema`
 * defaults `sortOrder` to `'desc'`, so a caller omitting it got the seven-day window ordered
 * furthest-out first. The browser never saw it — `useServerTable` always sends a direction — which
 * is precisely why it survived: the only test of the ordering passes `sortOrder=asc` itself.
 *
 * ⚠️ THE COST, STATED: `?sortBy=paDate` WITH NO `sortOrder` IS NOW OLDEST-FIRST. That is a
 * direct-API-caller change only (the Postman collection and anything hand-rolled); every screen
 * sends both terms, and `arrangementGroups.ts`'s nesting reads adjacency rather than direction.
 */
export const listArrangementsQuerySchema = createPaginationSchema(
  ARRANGEMENT_SORT_FIELDS,
  'firstDutyDate',
  'asc',
).extend({
  /** [PLACE-500] As long as the longest field it reads — a duty place — so pasting a whole place is not a 422. */
  search: z.string().trim().min(1).max(ARRANGEMENT_LIMITS.PLACE_NAME).optional(),
  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [amended P6d] ONE STATUS OR SEVERAL, AND THE WIDENING IS BACKWARDS-COMPATIBLE.
   *
   *  A single value still parses to a single value, so `web`'s existing control and every
   *  saved URL are untouched — this is why it is a union rather than a rename.
   *
   *  ⚠️ WHY IT HAD TO WIDEN: the officer app filters by the OFFICER-FACING STATE, because
   *  `officerDutyState.constant.ts` forbids any client rendering a raw status ("a client
   *  printing the raw value tells a constable their duty is 'allocation in progress'"). That
   *  reduction is MANY-TO-ONE — `CONFIRMED` covers `PUBLISHED` and `ALLOCATION_IN_PROGRESS`
   *  — so a single-valued filter cannot express it, and the alternatives were a SECOND status
   *  parameter (two vocabularies for one thing, which [LIFECYCLE] forbids) or a raw enum on a
   *  phone screen.
   *
   *  ⚠️ `z.preprocess`, BECAUSE `?status=X` ARRIVES AS A STRING AND `?status=X&status=Y` AS AN
   *  ARRAY. `dashboardQuerySchema.tiles` already does exactly this and for the same reason;
   *  without it a caller sending one value gets a type error rather than a filter.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  status: z
    .preprocess(
      (value) => (typeof value === 'string' ? [value] : value),
      z.array(z.enum(ARRANGEMENT_STATUSES)).min(1).max(ARRANGEMENT_STATUSES.length),
    )
    .optional(),
  /** P3b's second tab is this filter with a different value, and nothing else. */
  kind: z.enum(ARRANGEMENT_KINDS).default('STANDARD'),
  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [P7B §5b] UNTYPED BY DEFAULT, AND WITHOUT THE OPERATOR ASKING.
   *
   *  `none` lists only orders with no `type` — which is every order that existed before P7B, so the
   *  ordinary list reads exactly what it always read. A registered key lists that type: the
   *  `Durga Puja PA` list.
   *
   *  ⚠️ [amended TYPE-FILTER] `any` LISTS EVERY TYPE, AND THE DEFAULT IS STILL `none`. Manage
   *  Arrangements sends `any` by default and its "Order type" filter narrows it; every caller that
   *  names no type — the "Start from a previous one" picker, Postman — reads what it always read.
   *
   *  ⚠️ IT IS STILL NEVER ONE OF `ARRANGEMENT_SEARCH_HINT_FILTERS`, though it now has an "all" value: on
   *  the Durga Puja PA list it is the ROUTE, which a hint cannot relax, and the server cannot tell which
   *  list asked. Every hint probe keeps it as sent; the web names a narrowing type itself.
   *
   *  ⚠️ THE DEFAULT MAKES IT REQUIRED IN A HAND-BUILT QUERY — `officerOps.service.ts` passes `none`
   *  ([P7B] decision 4: Duty Mitra's operations list stays untyped), and the compiler is what made
   *  that a decision rather than an accident.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  type: z
    .union([z.literal(ARRANGEMENT_TYPE_NONE), z.literal(ARRANGEMENT_TYPE_ANY), z.enum(ARRANGEMENT_TYPE_KEYS)])
    .default(ARRANGEMENT_TYPE_NONE),
  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [DP2026] A DRAFT ITS OWN YEAR'S BOOKLET MADE LIVES ON THE TYPED LIST UNTIL IT IS PUBLISHED.
   *
   *  `exclude` leaves out every DRAFT whose `template_source.set_key` is its own `type` — the 2026
   *  booklet's `durga-puja-2026` drafts (`ARRANGEMENT_BOOKLETS`' header). Manage Arrangements sends it;
   *  published orders, drafts an EARLIER booklet made, and every untyped order are untouched. `include`
   *  — the default — adds no term, so every other caller reads what it always read, and the compiler
   *  makes a hand-built query name it (`officerOps.service.ts`).
   *
   *  ⚠️ NEVER A SEARCH-HINT FILTER: a probe keeps it, so a hint never offers a draft the page hides.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  currentBookletDrafts: z.enum(['include', 'exclude']).default('include'),
  /**
   * ⚠️ [DP2026] WHICH BOOKLET'S ORDERS — `all` (the default, no term), `current` (made by the type's own
   * year's booklet) or `earlier` (made by another booklet). It narrows only when `type` names a registered
   * type: the Durga Puja PA list. Never a search-hint filter, for `currentBookletDrafts`' reason.
   */
  booklet: z.enum(ARRANGEMENT_BOOKLETS).default('all'),
  policyId: objectIdSchema.optional(),
  /**
   * ⚠️ [P4] "WHAT DID THIS STANDING ORDER GENERATE?" — the P3b.1 carry-forward.
   *
   * The round-the-clock list showed a count nobody could click, and FR-5.5.5's rule is that
   * a number no one can click is a number no one trusts. This is that link, and it is a
   * FILTER on the list that already exists rather than a parallel endpoint.
   *
   * ⚠️ IT MUST TRAVEL WITH `kind=ROUND_THE_CLOCK`. A generated arrangement carries that kind
   * and `kind` DEFAULTS to `STANDARD` here, so this filter on its own lands on an empty
   * table — which reads as "the generator produced nothing".
   */
  standingOrderId: objectIdSchema.optional(),
  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [amended LIST-FILTERS] ONE DIVISION OR SEVERAL — THE SECOND LIST FILTER WIDENED THE
   *  `status` WAY, AND FOR THE SAME BACKWARDS-COMPATIBLE REASON.
   *
   *  "Adjust filters" on Manage Arrangements and Durga Puja PA narrows to any number of
   *  divisions, sent as REPEATED keys (`?divisionOrgUnitId=A&divisionOrgUnitId=B`, which
   *  client-core's `buildUrl` writes for an array). A single `?divisionOrgUnitId=X` still
   *  arrives as a string and still parses — to a one-element list — so every saved request and
   *  Duty Mitra's operations list (`officerOps.service.ts`, single-valued by decision) are
   *  untouched. A second parameter for "several divisions" would be two vocabularies for one
   *  filter, which [P6d] refused for `status`.
   *
   *  ⚠️ IT MATCHES `division_org_unit_ids` — the divisions an order asks FORCE of, the list's
   *  Divisions column — so several means "asks force of ANY of them".
   *
   *  ⚠️ `[]` IS A 422 (`.min(1)`), so the absent key is the only "no division" there is, and the
   *  search hint's relaxed form (`undefined`) removes the list WHOLE rather than half-clearing it.
   *  `.max` is `ARRANGEMENT_LIST_DIVISIONS_MAX` — headroom, not today's division count.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  divisionOrgUnitId: z
    .preprocess(
      (value) => (typeof value === 'string' ? [value] : value),
      z.array(objectIdSchema).min(1).max(ARRANGEMENT_LIST_DIVISIONS_MAX),
    )
    .optional(),
  dateFrom: calendarDateSchema.optional(),
  dateTo: calendarDateSchema.optional(),
  /**
   * The Archive. A FILTER rather than a second route, so the CP office reaches restorable
   * arrangements with the machinery they already know — and so P3b's round-the-clock tab
   * is a third value here rather than a third page.
   *
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [D4.4 §7b] THIS WAS `archived: z.stringbool()` AND IT IS WHY THE `Show` CONTROL
   *  DID NOTHING FOR A WHOLE RELEASE.
   *
   *  [LIFECYCLE] replaced `includeInactive`/`archived` with three-valued `show` and its
   *  changelog says "on all five lists". It reached four. `standingOrder`, `policy`,
   *  `postType` and `vehicleType` all took `show: showLifecycleSchema`; this file was never
   *  edited, and git confirms these lines were byte-identical from P3a until now.
   *
   *  ⚠️ THE FAILURE WAS SILENT IN BOTH DIRECTIONS, WHICH IS WHY NOTHING CAUGHT IT. The web
   *  shipped the control, put `show` in `filterKeys`, and genuinely sent `?show=archived` on
   *  the wire — so the client looked right in every review. The validation middleware then
   *  STRIPPED it, because unknown keys are stripped by default and that is the
   *  mass-assignment defence working exactly as designed on a key nobody had declared. No
   *  error, no 422, no log line: the list simply came back unchanged.
   *
   *  ⚠️ AND `show=all` WAS NOT MERELY UNWIRED, IT WAS UNREPRESENTABLE. A boolean has two
   *  values; the axis has three. That is [LIFECYCLE]'s own argument for the enum, and this
   *  list is the one that never got it.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  show: showLifecycleSchema,
});

// ── [C5b §6] The search hint ─────────────────────────────────────────────────────

type ListArrangementsQueryShape = z.infer<typeof listArrangementsQuerySchema>;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C5b §6] WHICH FILTERS A SEARCH HINT MAY NAME — IN KEEP-PRIORITY ORDER.
 *
 *  A search that found nothing because a FILTER hid its matches read "No arrangements yet." —
 *  the operator searched for an order they knew existed, got an empty table, and had no way to
 *  tell "no such order" from "the order is outside the seven-day window". `GET /arrangements`
 *  now answers that case with `searchHint.resetFilters`: the filters to clear, and nothing more.
 *
 *  ⚠️ KEEP-PRIORITY: the first is kept the LONGEST and reset last, because the planner re-adds
 *  the filters most-deliberate first and keeps each one that still finds a match.
 *
 *  ⚠️ `dates` LEADS, AND IT MUST. It is the one relaxation that is NOT monotone: the any-date
 *  window is a superset of every EXPLICIT window but not of the DEFAULT one, whose null arms admit
 *  an undated draft ([C3c §8a]). Probed first, at its widest, the default window produces no
 *  false negative.
 *
 *  ⚠️ `kind` IS NOT HERE AND MUST NOT BE ADDED. It is a TAB with no "all" value, so switching it
 *  is not a widening; every probe keeps `kind` exactly as sent, and the empty state names the tab
 *  instead of offering to switch it. `search` is not here either — keeping the term is the point.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const ARRANGEMENT_SEARCH_HINT_FILTERS = [
  'dates',
  'standingOrderId',
  'status',
  'show',
  'divisionOrgUnitId',
  'policyId',
] as const;
export type ArrangementSearchHintFilter = (typeof ARRANGEMENT_SEARCH_HINT_FILTERS)[number];

/**
 * ⚠️ [C5b §6] EACH FILTER'S RELAXED FORM IS EXACTLY WHAT THE CLIENT SENDS AFTER "Show matches".
 *
 * One table, read by both halves: the web applies it as a URL patch and the server PARSES the same
 * patch through `listArrangementsQuerySchema` before it probes, so a probe can never answer for a
 * request the client will not send.
 *
 * ⚠️ `dates` RELAXES TO THE ANY-DATE WINDOW, NEVER TO "CLEARED". Clearing lands on the DEFAULT
 * window, which loses an explicit range in the past — not a superset. [C3c.1]'s rule stands: there
 * is still no "no window" escape, and this adds none.
 *
 * ⚠️ `status` IS REMOVED, WHATEVER ITS SHAPE. `Published` arrives as a LIST of two statuses, and an
 * empty list is a 422 (`.min(1)`), so the absent key is the only relaxed form there is.
 */
export const ARRANGEMENT_SEARCH_HINT_RESET = {
  dates: ARRANGEMENT_ANY_DATE_WINDOW,
  standingOrderId: { standingOrderId: undefined },
  status: { status: undefined },
  show: { show: 'all' },
  divisionOrgUnitId: { divisionOrgUnitId: undefined },
  policyId: { policyId: undefined },
} as const satisfies Record<
  ArrangementSearchHintFilter,
  Partial<Record<keyof ListArrangementsQueryShape, string | undefined>>
>;

/**
 * ⚠️ [C5b §6] THE ONE KEY `GET /arrangements` MAY CARRY BESIDE THE FIVE PAGINATION KEYS.
 *
 * Present only when the page is EMPTY, a search term was sent, and at least one filter hid a
 * match inside the caller's own scope. Absent otherwise — including when the probes ran out of
 * budget, so its absence is never evidence that nothing exists.
 */
export const arrangementSearchHintSchema = z.object({
  resetFilters: z.array(z.enum(ARRANGEMENT_SEARCH_HINT_FILTERS)).min(1).max(ARRANGEMENT_SEARCH_HINT_FILTERS.length),
});

/**
 * The list page's extra keys, as a schema rather than a hand-typed field — `ArrangementListPageDto`
 * is `Paginated<ArrangementListItemDto>` intersected with its inference. `paginated()` never adds
 * it; `listArrangementsWithHint` spreads it onto the page.
 */
export const arrangementListPageExtrasSchema = z.object({
  searchHint: arrangementSearchHintSchema.optional(),
});

/** The URL patch the web writes for "Show matches" — the table's entries for `keys`, merged. */
export const searchHintPatch = (keys: readonly ArrangementSearchHintFilter[]): Record<string, string | undefined> =>
  Object.fromEntries(keys.flatMap((key) => Object.entries(ARRANGEMENT_SEARCH_HINT_RESET[key])));

/**
 * ⚠️ A FILTER IS ACTIVE WHEN APPLYING ITS PATCH WOULD CHANGE THE PARSED QUERY — in keep order.
 *
 * So `show` is always active unless it is already `all` (it DEFAULTS to `current`), and `dates` is
 * active unless the request already carries the any-date window — the DEFAULT window included.
 */
export const activeSearchHintFilters = (query: ListArrangementsQueryShape): ArrangementSearchHintFilter[] => {
  const values: Readonly<Record<string, unknown>> = query;
  return ARRANGEMENT_SEARCH_HINT_FILTERS.filter((filter) =>
    Object.entries(ARRANGEMENT_SEARCH_HINT_RESET[filter]).some(([key, relaxed]) => values[key] !== relaxed),
  );
};

/** ⚠️ PARSED, so the server probes exactly the request the client will send after the reset. */
export const relaxListArrangementsQuery = (
  query: ListArrangementsQueryShape,
  keys: readonly ArrangementSearchHintFilter[],
): ListArrangementsQueryShape => listArrangementsQuerySchema.parse({ ...query, ...searchHintPatch(keys) });

// ── Derivations shared by both sides ─────────────────────────────────────────────

/**
 * What a duty point is CALLED, when the operator has not named it. [amended D4]
 *
 * ⚠️ SHARED, for `arrangementTitle.util.ts`'s reason: the card's live heading and the
 * API's `displayTitle` are one sentence, and two implementations of "what do I call a
 * point with no name and no place yet" disagree on exactly the empty case that matters.
 *
 * The ordinal is 1-based because it is shown to a person. It is the FALLBACK, never a
 * stored value — a stored one goes stale the moment a card is reordered.
 */
export const dutyPointDisplayTitle = (
  point: { name?: string | null; places: readonly { name: string }[] },
  ordinal: number,
): string => {
  const named = point.name?.trim();
  if (named) return named;
  const places = point.places.map((place) => place.name.trim()).filter((name) => name.length > 0);
  if (places[0] === undefined) return `Duty point ${String(ordinal)}`;
  // A route reads as a route. One place reads as itself.
  return places.join(' → ');
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [P6c §6] THE PRINTED ORDER SHEET.
 *
 *  ⚠️ IT DOES NOT MATCH A DEPARTMENTAL SAMPLE, AND THAT IS A CLIENT DECISION (closing
 *  OD-12). Duty points make the layout navigable BY POLICY — an order that covers three
 *  places under two policies is three sections a reader can find, not one grid they have to
 *  scan. There is no departmental sheet to converge on, so this is our own design.
 *
 *  ⚠️ IT IS DECLARED HERE RATHER THAN IN A NEW FILE, FOR [D4]'s REASON, VERBATIM:
 *  `commandRoleIsolation.test.ts` CLAIM 5's allow-list already names this file, and a new
 *  one would have had to widen it for nothing. A printed order lists command — it must —
 *  so the vocabulary has to live somewhere that is already permitted to know it exists.
 *
 *  ⚠️ AND NOTHING HERE COUNTS A COMMAND ENTRY. `requiredCount` and `namedCount` below are
 *  FORCE totals (FR-5.2.6b, closing OD-9) and are declared on their own lines, never beside
 *  a command field. That is what CLAIM 5's per-line arithmetic scan actually checks.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/** One person on the sheet. Deliberately NOT `officerSummarySchema` — see the note below. */
export const orderSheetOfficerSchema = z.object({
  id: objectIdSchema,
  name: z.string(),
  rankShortName: z.string().nullable(),
  orgUnitName: z.string().nullable(),
  /** ⚠️ [C5 §7b] Printed beneath the name — see the header above this schema's command twin. */
  phone: z.string().nullable(),
  /**
   * ⚠️ [C5 §6] THE DIVISION ABOVE THE POSTING, BECAUSE `Nadial` DOES NOT SAY WHOSE IT IS.
   *
   * A printed order lists officers from every division that was asked, and a bare unit name
   * leaves a reader working out which one supplied them. Every SOURCE on this system already
   * carries both halves — `allocationSchema`, `fillAllocationSchema` and `previewDivisionRowSchema`
   * all do — and the officer's own POSTING was the gap.
   *
   * ⚠️ IT IS SENT, NEVER DERIVED IN THE CLIENT. §6: two clients deriving an ancestor separately
   * will disagree, and the PWA cannot import `web/src/lib/addressee.ts` in any case. The server
   * resolves it once through `loadDivisionsForOrgUnits`, which reads the stored `ancestors`
   * array in ONE batched query and already answers correctly for an officer posted TO a
   * division — that is a whole role, not an edge case ([P5b]).
   *
   * ⚠️ NULLABLE, because a unit outside any division is representable: the commissionerate's
   * own staff are posted at the root. A renderer joins on ` · ` and omits the separator.
   */
  orgUnitDivisionName: z.string().nullable(),
});

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [amended C5 §7b] THE PHONE IS ON THE SHEET NOW, AND THIS REVERSES A CONTROL HELD THREE
 *  WAYS. A CLIENT DECISION, WITH THE COST STATED RATHER THAN DISCOVERED.
 *
 *  What stood here: "NO PHONE NUMBER, AND THE OMISSION IS THE CONTROL RATHER THAN THE DTO.
 *  `loadOfficerSummaries` selects `phone`, so a sheet built on `officerSummarySchema` would
 *  carry every officer's number into a document whose entire purpose is to be PRINTED and left
 *  on a desk. [P5a §5b]'s argument applies with more force here than it did there: the service
 *  projects the column away rather than mapping it out."
 *
 *  All three mechanisms move together — this field, `loadSheetOfficers`' projection, and
 *  `orderSheet.test.ts`'s payload scan, which is INVERTED rather than deleted so the old rule
 *  stays findable. Leaving any one of them would have been the worst outcome: a field nothing
 *  fills, or a projection nothing reads.
 *
 *  ⚠️ THE COST, STATED. A 200-strong bandobast's numbers now print on paper that is handed
 *  round and left on desks, and there is no per-officer meter on this route — [C1b §5] meters
 *  every OTHER disclosure in the product per calling officer, against two ceilings, with an
 *  audit row each. What replaces that here is a single `ORDER_SHEET_VIEWED` row per request,
 *  because the sheet is ONE act rather than N: `OFFICER_DIRECTORY_VIEWED`'s own arithmetic, and
 *  its own rule that the row carries ids and COUNTS and never a name or a number.
 *
 *  ⚠️ AND IT IS NULLABLE, THOUGH `officers.phone` IS REQUIRED. An officer whose record could
 *  not be loaded already prints without a rank or a posting; a printed order must degrade to a
 *  missing line rather than refuse to render.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const orderSheetCommandRowSchema = z.object({
  /** Resolved from the frozen policy version, or the one-off row's own words. */
  label: z.string(),
  officers: z.array(orderSheetOfficerSchema),
});

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C5 §7a] ONE SOURCE'S SHARE OF ONE POST — the BLOCK a short-fall is stated inside.
 *
 *  The sheet had no per-source structure at all: each night was one flat table of post rows and
 *  `officers` was a single list, which `ArrangementPrint` recorded as decided with the client
 *  (CLAUDE.md divergence 708 — "the night's own table is the nearest block that exists"). §7a
 *  asks for the gap "WITHIN THE BLOCK, not collected at the end", and that needs a block.
 *
 *  ⚠️ IT COST NO NEW QUERY. `orderSheet.service.ts` already holds full `Deployment` documents,
 *  so `line.allocations` was in scope; and its `Assignment.find` is unprojected, so every named
 *  row already carried `alloc_key` while the builder read only `line_key`.
 *
 *  ⚠️ `allocKey` IS `fillRef.util.ts`'s OWN KEY, never a hand-built one — [C4a §3] shipped
 *  exactly that defect on `lineKey` and printed "Not yet named" over officers who were standing
 *  there. It is also what joins a block to its `shortfallNoteSchema` entry.
 *
 *  ⚠️ AND A SOURCE WITH NO NAMES STILL PRINTS. `requiredCount` is what was asked of it, so a
 *  block reading `0 of 6` is the most important thing on the page — dropping it would hide the
 *  division that supplied nobody, which is the direction that fails silently.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const orderSheetSourceSchema = z.object({
  /** `<division>:<unit|*>` — `fillRef.util.ts`'s own, so a block joins its short-fall note. */
  allocKey: z.string(),
  /** `Division` or `Division · Unit`, resolved server-side — never a hex id. */
  label: z.string(),
  requiredCount: z.number().int().min(0),
  namedCount: z.number().int().min(0),
  officers: z.array(orderSheetOfficerSchema),
  /**
   * ─────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C5c §6] WHETHER THE PEOPLE IN THIS BLOCK TURNED UP — printed beneath it in WORDS,
   *  `1 present · 1 absent · 0 not arrived`, because a photocopied mono sheet has no tint.
   *
   *  ⚠️ OPTIONAL, AND THE ABSENCE IS THE SIGNAL ([P6d], [C1e §2]): the key is omitted until the
   *  block's duty has STARTED (§6d — zeros on an order issued three days early read as a fault)
   *  and on a block naming nobody, where `Not yet named` already says everything. So "the key is
   *  here" means "print the line", decided once on the server rather than again in the page.
   *
   *  ⚠️ THE SAME FOLD AS THE PREVIEW's `presence` — `presenceOfRows` over the block's own named
   *  officers — so the three figures sum to `namedCount`, and paper and screen cannot disagree.
   * ─────────────────────────────────────────────────────────────────────────────
   */
  attendance: presenceTallySchema.optional(),
});

export const orderSheetPostRowSchema = z.object({
  /** The master's current name. A retired post type prints its name, never a blank. */
  label: z.string(),
  shortLabel: z.string(),
  /**
   * ⚠️ [P7A §3] WHAT THE LINE IS — the vocabulary `fillLineSchema.kind` and `previewRosterRowSchema.kind`
   * already use. A `vehicle` row prints what it REQUIRES and nothing else: its sources carry no
   * officers, `namedCount` is `0`, and no gap or attendance line is printed beneath it — a van is
   * counted, never named, so "Not yet named" or a short-fall under it would be a false sentence.
   */
  kind: z.enum(['post', 'vehicle', 'adhoc']),
  requiredCount: z.number().int().min(0),
  namedCount: z.number().int().min(0),
  /**
   * ⚠️ [amended C5 §7a] THE NAMES LIVE IN `sources[]` NOW, AND THIS IS A MOVE RATHER THAN AN
   * ADDITION. Carrying both a flat `officers` and the same people again per source would double
   * the heaviest payload in the product — `MAX_ORDER_SHEET_ASSIGNMENTS` is 20 000 rows — and
   * would be two answers to "who is on this post".
   *
   * ⚠️ A NAME WHOSE ALLOCATION ROW HAS GONE STILL PRINTS. `reconcileFill` can leave an
   * assignment whose `alloc_key` the current requirement no longer lists; it is collected into a
   * source of its own rather than dropped, because a person standing on the ground is not an
   * artefact of the plan. `orderSheet.service.ts` says which label it takes.
   */
  sources: z.array(orderSheetSourceSchema),
  /**
   * ⚠️ [C5c §2] THIS POST IS SHARED FROM THIS DUTY POINT ACROSS EVERY OTHER — printed after its
   * label so the page says why the other cards carry no row for it. Present only when true: an
   * ordinary sheet's bytes do not move.
   */
  sharedAcrossDutyPoints: z.literal(true).optional(),
});

export const orderSheetDutySchema = z.object({
  deploymentId: objectIdSchema,
  dutyDate: calendarDateSchema,
  slotLabel: z.string(),
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  /** ⚠️ So a whole-day duty never prints `00:00–00:00`. */
  hasFixedTime: z.boolean(),
  spansMidnight: z.boolean(),
  places: z.array(dutyPointPlaceSchema),
  reportingPlace: dutyPointPlaceSchema.nullable(),
  /**
   * ⚠️ [amended C1c §1a] STAMPED, so a re-issued sheet prints what the duty was issued with.
   * ⚠️ [amended C5b §3e] A fixed-time duty's is its OWN start — derived on read, so a legacy card
   * minimum never prints.
   */
  reportingTime: timeOfDaySchema.nullable(),
  /** [STAND-DOWN] From the stamped `stand_down_at`, rendered back to `HH:mm` in IST. */
  standDownTime: timeOfDaySchema.nullable(),
  posts: z.array(orderSheetPostRowSchema),
  /** ⚠️ [C4a §7b] Printed BENEATH this duty's own table, never collected at the end. */
  shortfalls: z.array(shortfallNoteSchema),
  commands: z.array(orderSheetCommandRowSchema),
  /**
   * ⚠️ [C6a] A ROLE ASKED OF A DIVISION AND STILL AWAITED ON THIS DUTY — printed as "In-charge:
   * awaited from ND", because a blank In-charge on paper does not explain itself to a commander
   * reading it on the ground. A SEPARATE array, so `orderSheetCommandRowSchema`'s pinned keys do not
   * move; present only when non-empty, so every other sheet is byte-identical. Words, never a number.
   *
   * ⚠️ [C6a review] `commandRow` SAYS WHICH NAMED LINE AN AWAITED ROLE BELONGS ON — the position in
   * THIS duty's `commands` of the stamped row with the same COMMAND KEY, absent when the role has no
   * named line. It is a pointer and never a figure. Matched by label, a card holding a policy
   * "Incharge" named by name and a one-off "Incharge" asked of CD printed the one-off's clause on the
   * policy role's line and gave the one-off none — `R:incharge` and `A:incharge` are two roles.
   */
  awaitedCommand: z.array(awaitedCommandSchema.extend({ commandRow: z.number().int().min(0).optional() })).optional(),
});

export const orderSheetDutyPointSchema = z.object({
  id: z.string(),
  ordinal: z.number().int().min(1),
  title: z.string(),
  places: z.array(dutyPointPlaceSchema),
  reportingPlace: dutyPointPlaceSchema.nullable(),
  /**
   * ⚠️ [amended C1c §1a] The card's own, beside its reporting PLACE. Either may stand alone.
   * ⚠️ [amended C5b §3e] `null` on a shift card, so `Report to` carries a time on a whole-day card only.
   */
  reportingTime: timeOfDaySchema.nullable(),
  /** [STAND-DOWN] The card's own stand-down time, or `null`. */
  standDownTime: timeOfDaySchema.nullable(),
  /** [C4a §4] The card's zone, printed in its header when there is one. */
  zone: z.string().nullable(),
  /** [C5c §4] The card's sector, printed WHOLE beneath the zone when there is one — paper has no hover. */
  sector: z.string().nullable(),
  policyName: z.string().nullable(),
  instructions: z.array(z.object({ type: z.literal('LINE'), text: z.string() })),
  supportingArrangements: z.array(z.object({ personName: z.string(), responsibilities: z.array(z.string()) })),
  duties: z.array(orderSheetDutySchema),
  /**
   * ⚠️ [C5c §2] THE COLUMNS THIS CARD DOES NOT ASK FOR BECAUSE ANOTHER CARD ASKS FOR THEM ONCE.
   *
   * Without it a covered card prints no row for its inspector at all, and a photocopied order
   * reads as though the place has none. Worded to survive mono print: the words carry it.
   * Present only when the card is covered, so an ordinary sheet is byte-identical.
   */
  coveredBy: z
    .array(
      z.object({
        label: z.string(),
        shortLabel: z.string(),
        anchorOrdinal: z.number().int().min(1),
        anchorTitle: z.string(),
      }),
    )
    .optional(),
});

export const orderSheetSchema = z.object({
  id: objectIdSchema,
  reference: z.string(),
  /**
   * ⚠️ [C2b §8b] THE DEPARTMENT'S OWN NUMBER, ON THE ARTEFACT THEY ACTUALLY FILE.
   *
   * It was absent from this schema entirely, so the PRINTED order — the sheet that goes out
   * on paper — was headed with the system reference and nothing else, while every screen in
   * the dashboard showed `paNumber ?? reference`. The one place the number is written down by
   * hand was the one place it could not be read back.
   *
   * ⚠️ NULLABLE AND REQUIRED, not optional. It is operator-typed and genuinely absent on plenty
   * of orders, and the sheet renders a labelled row either way — which is §6's own test for
   * which of the two to reach for.
   *
   * ⚠️ AND IT DOES NOT REPLACE `reference`. Both print. `paNumber ?? reference` is right for a
   * dense list cell and wrong here: the sheet is the record, and the record carries both the
   * number the department wrote and the one this system minted.
   */
  paNumber: z.string().nullable(),
  /**
   * ⚠️ [C2b §4f] WHAT THIS ORDER CORRECTS, AND IT IS ON THE SHEET BECAUSE THE SHEET IS WHAT
   * GOES OUT. A corrigendum circulated without naming the order it amends is a second set of
   * instructions for the same night with no way to tell which supersedes which — which is the
   * whole reason a corrigendum has a number of its own.
   *
   * ⚠️ THE PARENT ONLY, AND NOT THE FAMILY. A corrected order's own sheet says nothing: it was
   * printed and filed before the correction existed, and reprinting it with "superseded in part"
   * would be a claim the sheet cannot qualify. The corrigendum is the document that carries the
   * relationship, in the direction it actually runs.
   */
  correctsReference: z.string().nullable(),
  displayTitle: z.string(),
  /** ⚠️ ADMINISTRATIVE — "the order was dated X". Never a duty date. [D4] */
  paDate: calendarDateSchema,
  status: z.enum(ARRANGEMENT_STATUSES),
  description: z.string().nullable(),
  /** ⚠️ A STRING. The model refuses to invite arithmetic between a crowd ESTIMATE and a force count. */
  estimatedCrowdSize: z.string().nullable(),
  /** PA-level. A duty point's own are on the point — both levels print. [D4.3] */
  instructions: z.array(z.object({ type: z.literal('LINE'), text: z.string() })),
  supportingArrangements: z.array(z.object({ personName: z.string(), responsibilities: z.array(z.string()) })),
  dutyPoints: z.array(orderSheetDutyPointSchema),
  /** When the sheet was rendered. A printed page with no date is a page nobody can file. */
  generatedAt: z.iso.datetime(),
});
